import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../../../hooks/useApi';
import { useTheme } from '../../../context/ThemeContext';
import { useAuth } from '../../../context/AuthContext';
import { X, Plus, AlertCircle, MessageSquare, DollarSign, FileText, Send, Trash2, Check, Clock, Package, CheckCircle2, Archive, Wrench, Image, Upload, Info, ChevronDown, ChevronUp, Camera, ArrowLeft, LayoutGrid, List } from 'lucide-react';
import { getAssetImageUrl } from '../../../utils/assetImageUrl';
import { usePermissions } from '../../../hooks/usePermissions';
import { isCustomerContext, isVendorContext } from '../../../utils/repairContext';
import { ChatToggle } from '../../../components/ChatThread';
import ChatThread from '../../../components/ChatThread';
import SearchableSelect from '../../../components/SearchableSelect';
import ImageAttachment, { AttachmentDisplay } from '../../../components/ImageAttachment';
import RepairSummaryReport from '../../../components/RepairSummaryReport';
import PLXScanner from '../../../components/PLXScanner';
import api from '../../../lib/api';
import FeatureGate from '../../../components/FeatureGate';
import RepairListView from '../../../components/RepairListView';

const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];
const STATUS_OPTIONS = ['Sent to Vendor', 'Needs Approval', 'In Repair', 'Complete'];
const DAMAGE_TYPES = ['Physical', 'Electrical', 'Mechanical', 'Water Damage', 'Wear & Tear', 'Other'];

// Default checklist for repair form
const REPAIR_ITEMS = [
  { id: 'base', label: 'Base', cost: 200 },
  { id: 'forkliftGuide1', label: 'Forklift Guide 1', cost: 60 },
  { id: 'forkliftGuide2', label: 'Forklift Guide 2', cost: 60 },
  { id: 'door1', label: 'Door 1', cost: 90 },
  { id: 'door2', label: 'Door 2', cost: 90 },
  { id: 'corner1', label: 'Corner 1', cost: 25 },
  { id: 'corner2', label: 'Corner 2', cost: 25 },
  { id: 'corner3', label: 'Corner 3', cost: 25 },
  { id: 'corner4', label: 'Corner 4', cost: 25 },
  { id: 'generalRepair', label: 'General Repair', cost: 45 },
];

// CSS to hide scrollbars while keeping scroll functionality
const hideScrollbarStyle = `
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
  .hide-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

// Assigned Vendor Inspection Timeline - shown when assigned vendor views "Sent to Vendor" repair
const AssignedVendorInspectionTimeline = ({ 
  ticket, 
  setShowVendorInspectionForm,
  setVendorInspectionTicket,
  setSummaryTicket, 
  setSelectedTicket, 
  isDark
}) => {
  const assetImageUrl = ticket.asset_image_url || 
    (ticket.asset_type ? getAssetImageUrl(ticket.asset_type + '.png') : null);

  return (
    <div className={`flex flex-col h-full rounded-xl border overflow-hidden ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
      {/* Header */}
      <div className={`flex items-center justify-between p-4 sm:p-6 border-b ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <div className="flex items-center gap-4">
          {assetImageUrl && (
            <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-slate-800 rounded-xl overflow-hidden flex-shrink-0 border-2 ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
              <img 
                src={assetImageUrl} 
                alt={ticket.asset_name || 'Asset'} 
                className="w-full h-full object-contain p-2"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-white">{ticket.asset_name || ticket.asset_id}</h2>
            <p className="text-xs sm:text-sm text-slate-400 mt-1">
              Repair Request from Owning Organization
            </p>
          </div>
        </div>
        <button 
          onClick={() => { setSummaryTicket(null); setSelectedTicket(null); setShowVendorInspectionForm(false); }}
          className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors"
        >
          <X size={18} className="text-slate-400" />
        </button>
      </div>

      {/* Timeline Content - Scrollable */}
      <div className="flex-1 overflow-y-auto py-6 sm:py-8 px-4 sm:px-8 hide-scrollbar">
        <div className="relative max-w-4xl mx-auto w-full">
          {/* Vertical Timeline Line */}
          <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-0.5 h-full bg-slate-700/50" />
          
          {/* Timeline Items */}
          <div className="space-y-8 w-full">
            {/* Step 1: Inspection (current step) */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-red-500 bg-red-500/20 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:ml-auto sm:pr-8 sm:text-right">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-red-500 bg-red-500/20 shadow-lg hover:shadow-xl transition-all">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3 sm:justify-end">
                    <Wrench size={18} className="text-red-400" />
                    <h3 className="text-base sm:text-lg font-bold text-red-400">
                      Inspection Required
                    </h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-400 leading-relaxed mb-4">
                    Please review the asset and fill out the inspection form with damage details and repair cost estimate.
                  </p>
                  <button
                    onClick={() => {
                      setShowVendorInspectionForm(true);
                      setVendorInspectionTicket(ticket);
                    }}
                    className="w-full px-4 py-2.5 bg-red-600 hover:bg-red-500 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2 shadow-lg"
                  >
                    <Wrench size={16} />
                    Fill Out Inspection
                  </button>
                </div>
              </div>
            </div>

            {/* Step 2: Awaiting Approval */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-slate-600 bg-slate-800/30 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:mr-auto sm:pl-8 sm:text-left">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-slate-700 bg-slate-800/20 shadow-lg opacity-60">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <Clock size={18} className="text-slate-600" />
                    <h3 className="text-base sm:text-lg font-bold text-slate-500">Awaiting Approval</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-500 italic">Not yet reached</p>
                </div>
              </div>
            </div>

            {/* Step 3: In Repair */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-slate-600 bg-slate-800/30 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:ml-auto sm:pr-8 sm:text-right">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-slate-700 bg-slate-800/20 shadow-lg opacity-60">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3 sm:justify-end">
                    <Package size={18} className="text-slate-600" />
                    <h3 className="text-base sm:text-lg font-bold text-slate-500">In Repair</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-500 italic">Not yet reached</p>
                </div>
              </div>
            </div>

            {/* Step 4: Complete */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-slate-600 bg-slate-800/30 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:mr-auto sm:pl-8 sm:text-left">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-slate-700 bg-slate-800/20 shadow-lg opacity-60">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <Check size={18} className="text-slate-600" />
                    <h3 className="text-base sm:text-lg font-bold text-slate-500">Complete</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-500 italic">Not yet reached</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Assigned Vendor Waiting Timeline - shown when assigned vendor views "Needs Approval" repair (waiting for owning organization)
const VendorWaitingForApprovalTimeline = ({ 
  ticket, 
  setSummaryTicket, 
  setSelectedTicket, 
  isDark 
}) => {
  const assetImageUrl = ticket.asset_image_url || 
    (ticket.asset_type ? getAssetImageUrl(ticket.asset_type + '.png') : null);

  return (
    <div className={`flex flex-col h-full rounded-xl border overflow-hidden ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
      {/* Header */}
      <div className={`flex items-center justify-between p-4 sm:p-6 border-b ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <div className="flex items-center gap-4">
          {assetImageUrl && (
            <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-slate-800 rounded-xl overflow-hidden flex-shrink-0 border-2 ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
              <img 
                src={assetImageUrl} 
                alt={ticket.asset_name || 'Asset'} 
                className="w-full h-full object-contain p-2"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-white">{ticket.asset_name || ticket.asset_id}</h2>
            <p className="text-xs sm:text-sm text-slate-400 mt-1">
              Estimate Sent: ${parseFloat(ticket.total_repair_cost || 0).toFixed(2)}
            </p>
          </div>
        </div>
        <button 
          onClick={() => { setSummaryTicket(null); setSelectedTicket(null); }}
          className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors"
        >
          <X size={18} className="text-slate-400" />
        </button>
      </div>

      {/* Timeline Content - Scrollable */}
      <div className="flex-1 overflow-y-auto py-6 sm:py-8 px-4 sm:px-8 hide-scrollbar">
        <div className="relative max-w-4xl mx-auto w-full">
          {/* Vertical Timeline Line */}
          <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-0.5 h-full bg-slate-700/50" />

          {/* Timeline Items */}
          <div className="space-y-8 w-full">
            {/* Step 1: Inspection (completed) */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-red-500 bg-red-500/20 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:ml-auto sm:pr-8 sm:text-right">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-red-500/50 bg-red-500/10 shadow-lg">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3 sm:justify-end">
                    <CheckCircle2 size={18} className="text-red-400" />
                    <h3 className="text-base sm:text-lg font-bold text-red-400">Inspection Complete</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-400">You completed the inspection</p>
                  {ticket.needs_approval_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      {new Date(ticket.needs_approval_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Step 2: Awaiting Approval (current step) */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-yellow-500 bg-yellow-500/20 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:mr-auto sm:pl-8 sm:text-left">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-yellow-500 bg-yellow-500/20 shadow-lg hover:shadow-xl transition-all">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <Clock size={18} className="text-yellow-400" />
                    <h3 className="text-base sm:text-lg font-bold text-yellow-400">Awaiting Owner Approval</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                    The owning organization is reviewing your repair estimate. You'll be notified once they approve or decline.
                  </p>
                  {ticket.needs_approval_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      {new Date(ticket.needs_approval_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Step 3: In Repair */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-slate-600 bg-slate-800/30 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:ml-auto sm:pr-8 sm:text-right">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-slate-700 bg-slate-800/20 shadow-lg opacity-60">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3 sm:justify-end">
                    <Package size={18} className="text-slate-600" />
                    <h3 className="text-base sm:text-lg font-bold text-slate-500">In Repair</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-500 italic">Not yet reached</p>
                </div>
              </div>
            </div>

            {/* Step 4: Complete */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-slate-600 bg-slate-800/30 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:mr-auto sm:pl-8 sm:text-left">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-slate-700 bg-slate-800/20 shadow-lg opacity-60">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <Check size={18} className="text-slate-600" />
                    <h3 className="text-base sm:text-lg font-bold text-slate-500">Complete</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-500 italic">Not yet reached</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Owner Approval Timeline - shown when owning organization views "Needs Approval" repair
const CustomerApprovalTimeline = ({
  ticket,
  moveRepair,
  setSummaryTicket,
  setSelectedTicket,
  isDark
}) => {
  const assetImageUrl = ticket.asset_image_url || 
    (ticket.asset_type ? getAssetImageUrl(ticket.asset_type + '.png') : null);

  const handleApprove = async () => {
    try {
      await moveRepair(ticket.id, 'In Repair');
    } catch (error) {
      console.error('Error approving repair:', error);
      alert('Failed to approve repair. Please try again.');
    }
  };

  const handleDecline = async () => {
    if (window.confirm('Are you sure you want to decline this repair? It will be sent back to the assigned vendor for revision.')) {
      try {
        await moveRepair(ticket.id, 'Sent to Vendor');
        setSummaryTicket(prev => prev ? { ...prev, status: 'Sent to Vendor' } : null);
      } catch (error) {
        console.error('Error declining repair:', error);
        alert('Failed to decline repair. Please try again.');
      }
    }
  };

  return (
    <div className={`flex flex-col h-full rounded-xl border overflow-hidden ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
      {/* Header */}
      <div className={`flex items-center justify-between p-4 sm:p-6 border-b ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <div className="flex items-center gap-4">
          {assetImageUrl && (
            <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-slate-800 rounded-xl overflow-hidden flex-shrink-0 border-2 ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
              <img 
                src={assetImageUrl} 
                alt={ticket.asset_name || 'Asset'} 
                className="w-full h-full object-contain p-2"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-white">{ticket.asset_name || ticket.asset_id}</h2>
            <p className="text-xs sm:text-sm text-slate-400 mt-1">
              Repair Estimate: ${parseFloat(ticket.total_repair_cost || 0).toFixed(2)}
            </p>
          </div>
        </div>
        <button 
          onClick={() => { setSummaryTicket(null); setSelectedTicket(null); }}
          className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors"
        >
          <X size={18} className="text-slate-400" />
        </button>
      </div>

      {/* Timeline Content - Scrollable */}
      <div className="flex-1 overflow-y-auto py-6 sm:py-8 px-4 sm:px-8 hide-scrollbar">
        <div className="relative max-w-4xl mx-auto w-full">
          {/* Vertical Timeline Line */}
          <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-0.5 h-full bg-slate-700/50" />

          {/* Timeline Items */}
          <div className="space-y-8 w-full">
            {/* Step 1: Inspection (completed) */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-red-500 bg-red-500/20 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:ml-auto sm:pr-8 sm:text-right">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-red-500/50 bg-red-500/10 shadow-lg">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3 sm:justify-end">
                    <CheckCircle2 size={18} className="text-red-400" />
                    <h3 className="text-base sm:text-lg font-bold text-red-400">Inspection Complete</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-400">Assigned vendor has completed the inspection</p>
                  {ticket.needs_approval_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      {new Date(ticket.needs_approval_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Step 2: Awaiting Approval (current step) */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-yellow-500 bg-yellow-500/20 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:mr-auto sm:pl-8 sm:text-left">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-yellow-500 bg-yellow-500/20 shadow-lg hover:shadow-xl transition-all">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <Clock size={18} className="text-yellow-400" />
                    <h3 className="text-base sm:text-lg font-bold text-yellow-400">Your Approval Required</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-400 leading-relaxed mb-4">
                    Review the repair estimate and approve to proceed with repairs, or decline to send back for revision.
                  </p>
                  
                  {/* Repair Details */}
                  {ticket.damage_details && (
                    <div className="mb-4 p-3 bg-slate-900/40 rounded-lg border border-slate-700">
                      <div className="text-xs text-slate-500 uppercase mb-2">Repair Details</div>
                      {ticket.damage_details.description && (
                        <p className="text-sm text-slate-300 mb-2">{ticket.damage_details.description}</p>
                      )}
                      <div className="text-lg font-bold text-emerald-400">
                        Total Cost: ${parseFloat(ticket.total_repair_cost || 0).toFixed(2)}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={handleApprove}
                      className="flex-1 px-4 py-2.5 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2 shadow-lg"
                    >
                      <Check size={16} />
                      Approve Repair
                    </button>
                    <button
                      onClick={handleDecline}
                      className="flex-1 px-4 py-2.5 bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white border border-red-500/50 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                    >
                      <X size={16} />
                      Decline
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3: In Repair */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-slate-600 bg-slate-800/30 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:ml-auto sm:pr-8 sm:text-right">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-slate-700 bg-slate-800/20 shadow-lg opacity-60">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3 sm:justify-end">
                    <Package size={18} className="text-slate-600" />
                    <h3 className="text-base sm:text-lg font-bold text-slate-500">In Repair</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-500 italic">Not yet reached</p>
                </div>
              </div>
            </div>

            {/* Step 4: Complete */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-slate-600 bg-slate-800/30 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:mr-auto sm:pl-8 sm:text-left">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-slate-700 bg-slate-800/20 shadow-lg opacity-60">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <Check size={18} className="text-slate-600" />
                    <h3 className="text-base sm:text-lg font-bold text-slate-500">Complete</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-500 italic">Not yet reached</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Assigned Vendor Completion Timeline - shown when assigned vendor views "In Repair" repair
const VendorCompletionTimeline = ({ 
  ticket, 
  moveRepair, 
  setSummaryTicket, 
  setSelectedTicket, 
  isDark
}) => {
  const assetImageUrl = ticket.asset_image_url || 
    (ticket.asset_type ? getAssetImageUrl(ticket.asset_type + '.png') : null);

  const handleComplete = async () => {
    try {
      await moveRepair(ticket.id, 'Complete');
    } catch (error) {
      console.error('Error completing repair:', error);
      alert('Failed to mark repair as complete. Please try again.');
    }
  };

  const handleScrap = async () => {
    if (window.confirm('Are you sure you want to scrap this asset? This action cannot be undone.')) {
      try {
        await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/api/repairs/${ticket.id}/scrap`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setSummaryTicket(null);
        setSelectedTicket(null);
      } catch (error) {
        console.error('Error scrapping asset:', error);
        alert('Failed to scrap asset. Please try again.');
      }
    }
  };

  return (
    <div className={`flex flex-col h-full rounded-xl border overflow-hidden ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
      {/* Header */}
      <div className={`flex items-center justify-between p-4 sm:p-6 border-b ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <div className="flex items-center gap-4">
          {assetImageUrl && (
            <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-slate-800 rounded-xl overflow-hidden flex-shrink-0 border-2 ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
              <img 
                src={assetImageUrl} 
                alt={ticket.asset_name || 'Asset'} 
                className="w-full h-full object-contain p-2"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-white">{ticket.asset_name || ticket.asset_id}</h2>
            <p className="text-xs sm:text-sm text-slate-400 mt-1">
              Repair Cost: ${parseFloat(ticket.total_repair_cost || 0).toFixed(2)}
            </p>
          </div>
        </div>
        <button 
          onClick={() => { setSummaryTicket(null); setSelectedTicket(null); }}
          className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors"
        >
          <X size={18} className="text-slate-400" />
        </button>
      </div>

      {/* Timeline Content - Scrollable */}
      <div className="flex-1 overflow-y-auto py-6 sm:py-8 px-4 sm:px-8 hide-scrollbar">
        <div className="relative max-w-4xl mx-auto w-full">
          {/* Vertical Timeline Line */}
          <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-0.5 h-full bg-slate-700/50" />

          {/* Timeline Items */}
          <div className="space-y-8 w-full">
            {/* Step 1: Inspection (completed) */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-red-500 bg-red-500/20 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:ml-auto sm:pr-8 sm:text-right">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-red-500/50 bg-red-500/10 shadow-lg">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3 sm:justify-end">
                    <CheckCircle2 size={18} className="text-red-400" />
                    <h3 className="text-base sm:text-lg font-bold text-red-400">Inspection Complete</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-400">Initial inspection completed</p>
                  {ticket.needs_approval_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      {new Date(ticket.needs_approval_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Step 2: Approval (completed) */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-yellow-500 bg-yellow-500/20 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:mr-auto sm:pl-8 sm:text-left">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-yellow-500/50 bg-yellow-500/10 shadow-lg">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <CheckCircle2 size={18} className="text-yellow-400" />
                    <h3 className="text-base sm:text-lg font-bold text-yellow-400">Approved by Owner</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-400">Owner approved the repair</p>
                  {ticket.in_repair_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      {new Date(ticket.in_repair_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Step 3: In Repair (current step) */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-blue-500 bg-blue-500/20 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:ml-auto sm:pr-8 sm:text-right">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-blue-500 bg-blue-500/20 shadow-lg hover:shadow-xl transition-all">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3 sm:justify-end">
                    <Wrench size={18} className="text-blue-400" />
                    <h3 className="text-base sm:text-lg font-bold text-blue-400">Repair in Progress</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-400 leading-relaxed mb-4">
                    Complete the repair work and mark as complete when finished.
                  </p>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={handleComplete}
                      className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2 shadow-lg"
                    >
                      <Check size={16} />
                      Mark Complete
                    </button>
                    <button
                      onClick={handleScrap}
                      className="flex-1 px-4 py-2.5 bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white border border-red-500/50 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                    >
                      <Trash2 size={16} />
                      Scrap
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4: Complete */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-slate-600 bg-slate-800/30 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:mr-auto sm:pl-8 sm:text-left">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-slate-700 bg-slate-800/20 shadow-lg opacity-60">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <Check size={18} className="text-slate-600" />
                    <h3 className="text-base sm:text-lg font-bold text-slate-500">Complete</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-500 italic">Not yet reached</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Owner Waiting Timeline - shown when owning organization views "In Repair" repair (waiting for assigned vendor)
const CustomerWaitingForCompletionTimeline = ({ 
  ticket, 
  setSummaryTicket, 
  setSelectedTicket, 
  isDark 
}) => {
  const assetImageUrl = ticket.asset_image_url || 
    (ticket.asset_type ? getAssetImageUrl(ticket.asset_type + '.png') : null);

  return (
    <div className={`flex flex-col h-full rounded-xl border overflow-hidden ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
      {/* Header */}
      <div className={`flex items-center justify-between p-4 sm:p-6 border-b ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <div className="flex items-center gap-4">
          {assetImageUrl && (
            <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-slate-800 rounded-xl overflow-hidden flex-shrink-0 border-2 ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
              <img
                src={assetImageUrl}
                alt={ticket.asset_name || 'Asset'}
                className="w-full h-full object-contain p-2"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-white">{ticket.asset_name || ticket.asset_id}</h2>
            <p className="text-xs sm:text-sm text-slate-400 mt-1">
              Repair Cost: ${parseFloat(ticket.total_repair_cost || 0).toFixed(2)}
            </p>
          </div>
        </div>
        <button
          onClick={() => { setSummaryTicket(null); setSelectedTicket(null); }}
          className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors"
        >
          <X size={18} className="text-slate-400" />
        </button>
      </div>

      {/* Timeline Content - Scrollable */}
      <div className="flex-1 overflow-y-auto py-6 sm:py-8 px-4 sm:px-8 hide-scrollbar">
        <div className="relative max-w-4xl mx-auto w-full">
          {/* Vertical Timeline Line */}
          <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-0.5 h-full bg-slate-700/50" />

          {/* Timeline Items */}
          <div className="space-y-8 w-full">
            {/* Step 1: Inspection (completed) */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-red-500 bg-red-500/20 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:ml-auto sm:pr-8 sm:text-right">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-red-500/50 bg-red-500/10 shadow-lg">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3 sm:justify-end">
                    <CheckCircle2 size={18} className="text-red-400" />
                    <h3 className="text-base sm:text-lg font-bold text-red-400">Inspection Complete</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-400">Assigned vendor completed the inspection</p>
                  {ticket.needs_approval_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      {new Date(ticket.needs_approval_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Step 2: Approval (completed) */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-yellow-500 bg-yellow-500/20 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:mr-auto sm:pl-8 sm:text-left">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-yellow-500/50 bg-yellow-500/10 shadow-lg">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <CheckCircle2 size={18} className="text-yellow-400" />
                    <h3 className="text-base sm:text-lg font-bold text-yellow-400">You Approved</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-400">You approved the repair estimate</p>
                  {ticket.in_repair_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      {new Date(ticket.in_repair_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Step 3: In Repair (current step - waiting) */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-blue-500 bg-blue-500/20 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:ml-auto sm:pr-8 sm:text-right">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-blue-500 bg-blue-500/20 shadow-lg hover:shadow-xl transition-all">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3 sm:justify-end">
                    <Wrench size={18} className="text-blue-400" />
                    <h3 className="text-base sm:text-lg font-bold text-blue-400">Repair in Progress</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                    The assigned vendor is currently working on your repair. You'll be notified when it's complete.
                  </p>
                  {ticket.in_repair_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      {new Date(ticket.in_repair_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Step 4: Complete */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-slate-600 bg-slate-800/30 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:mr-auto sm:pl-8 sm:text-left">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-slate-700 bg-slate-800/20 shadow-lg opacity-60">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <Check size={18} className="text-slate-600" />
                    <h3 className="text-base sm:text-lg font-bold text-slate-500">Complete</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-500 italic">Not yet reached</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Complete Summary Timeline - shown when viewing "Complete" or "Finalized" repair
const CompleteSummaryTimeline = ({ 
  ticket, 
  setSummaryTicket, 
  setSelectedTicket, 
  finalizeRepair, 
  updating, 
  isCustomer, 
  isDark,
  setShowSummaryReport
}) => {
  const assetImageUrl = ticket.asset_image_url || 
    (ticket.asset_type ? getAssetImageUrl(ticket.asset_type + '.png') : null);

  // Helper to get timestamp for "Sent to Vendor" status
  const getSentToVendorTimestamp = () => {
    if (ticket.sent_to_vendor_at) {
      return ticket.sent_to_vendor_at;
    }
    if (ticket.status_history) {
      const sentToVendorEntry = ticket.status_history.find(h => h.status === 'Sent to Vendor' || h.status === 'Needs Repair');
      if (sentToVendorEntry) {
        return sentToVendorEntry.timestamp;
      }
    }
    return null;
  };

  // Calculate repair duration
  const getRepairDuration = () => {
    if (!ticket.status_history || ticket.status_history.length === 0) return null;
    
    const startEntry = ticket.status_history.find(h => h.status === 'In Repair');
    const endEntry = ticket.status_history.find(h => h.status === 'Complete');
    
    if (!startEntry || !endEntry) return null;
    
    const start = new Date(startEntry.timestamp);
    const end = new Date(endEntry.timestamp);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  };

  return (
    <div className={`flex flex-col h-full rounded-xl border overflow-hidden ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
      {/* Header */}
      <div className={`flex items-center justify-between p-4 sm:p-6 border-b ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <div className="flex items-center gap-4">
          {assetImageUrl && (
            <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-slate-800 rounded-xl overflow-hidden flex-shrink-0 border-2 ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
              <img 
                src={assetImageUrl} 
                alt={ticket.asset_name || 'Asset'} 
                className="w-full h-full object-contain p-2"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-white">{ticket.asset_name || ticket.asset_id}</h2>
            <p className="text-xs sm:text-sm text-slate-400 mt-1">
              Repair Complete • ${parseFloat(ticket.total_repair_cost || 0).toFixed(2)} • {getRepairDuration() || 'Duration N/A'}
            </p>
          </div>
        </div>
        <button 
          onClick={() => { setSummaryTicket(null); setSelectedTicket(null); }}
          className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors"
        >
          <X size={18} className="text-slate-400" />
        </button>
      </div>

      {/* Timeline Content - Scrollable */}
      <div className="flex-1 overflow-y-auto py-6 sm:py-8 px-4 sm:px-8 hide-scrollbar">
        <div className="relative max-w-4xl mx-auto w-full">
          {/* Vertical Timeline Line */}
          <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-0.5 h-full bg-slate-700/50" />

          {/* Timeline Items */}
          <div className="space-y-8 w-full">
            {/* Step 1: Inspection Complete */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-red-500 bg-red-500/20 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:ml-auto sm:pr-8 sm:text-right">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-red-500/50 bg-red-500/10 shadow-lg">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3 sm:justify-end">
                    <CheckCircle2 size={18} className="text-red-400" />
                    <h3 className="text-base sm:text-lg font-bold text-red-400">Inspection Complete</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-400">Assigned vendor completed damage inspection</p>
                  {ticket.needs_approval_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      {new Date(ticket.needs_approval_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Step 2: Approved by Customer */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-yellow-500 bg-yellow-500/20 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:mr-auto sm:pl-8 sm:text-left">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-yellow-500/50 bg-yellow-500/10 shadow-lg">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <CheckCircle2 size={18} className="text-yellow-400" />
                    <h3 className="text-base sm:text-lg font-bold text-yellow-400">Approved by Owner</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-400">Customer approved repair estimate</p>
                  {ticket.in_repair_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      {new Date(ticket.in_repair_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Step 3: Repair Complete */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-blue-500 bg-blue-500/20 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:ml-auto sm:pr-8 sm:text-right">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-blue-500/50 bg-blue-500/10 shadow-lg">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3 sm:justify-end">
                    <CheckCircle2 size={18} className="text-blue-400" />
                    <h3 className="text-base sm:text-lg font-bold text-blue-400">Repair Complete</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-400">Assigned vendor completed the repair work</p>
                  {ticket.completed_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      {new Date(ticket.completed_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Step 4: Complete */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-emerald-500 bg-emerald-500/20 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:mr-auto sm:pl-8 sm:text-left">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-emerald-500 bg-emerald-500/20 shadow-lg">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <CheckCircle2 size={18} className="text-emerald-400" />
                    <h3 className="text-base sm:text-lg font-bold text-emerald-400">Complete</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-400">Repair closed and archived</p>
                  {ticket.completed_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      {new Date(ticket.completed_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons - Fixed at bottom */}
      <div className={`border-t p-4 sm:p-6 flex justify-center gap-3 shrink-0 ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
        <button
          onClick={async () => {
            if (window.confirm('Archive this repair? It will be removed from the active workflow.')) {
              await finalizeRepair(ticket.id);
            }
          }}
          disabled={updating[ticket.id]}
          className="px-4 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {updating[ticket.id] ? (
            <>
              <div className="w-4 h-4 border-2 border-slate-400/30 border-t-slate-400 rounded-full animate-spin"></div>
              Archiving...
            </>
          ) : (
            <>
              <Archive size={16} />
              Archive Repair
            </>
          )}
        </button>
        <button
          onClick={() => setShowSummaryReport(ticket)}
          className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors flex items-center gap-2"
        >
          <FileText size={16} />
          View Summary Report
        </button>
      </div>
    </div>
  );
};

// Column Details Modal - shows all repairs in a column
const ColumnDetailsModal = ({
  columnId,
  columnTitle,
  repairs,
  isDark,
  onClose,
  onSelectRepair
}) => {
  if (!repairs || repairs.length === 0) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`border rounded-xl w-full max-w-3xl max-h-[80vh] shadow-2xl flex flex-col ${isDark ? 'bg-gray-800/95 border-gray-700' : 'bg-white border-gray-200'}`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b shrink-0 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {columnTitle}
            </h2>
            <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              {repairs.length} repair{repairs.length !== 1 ? 's' : ''} in this stage
            </p>
          </div>
          <button 
            onClick={onClose}
            className={`p-2 hover:bg-slate-800 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
          >
            <X size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 hide-scrollbar">
          <div className="space-y-3">
            {repairs.map((repair) => {
              const assetImageUrl = repair.asset_image_url || 
                (repair.asset_type ? getAssetImageUrl(repair.asset_type + '.png') : null);
              
              return (
                <div
                  key={repair.id}
                  onClick={() => {
                    onSelectRepair(repair);
                    onClose();
                  }}
                  className={`border rounded-lg p-4 cursor-pointer hover:opacity-80 transition-all ${isDark ? 'bg-slate-900/50 border-slate-700 hover:border-slate-600' : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
                >
                  <div className="flex items-center gap-4">
                    {assetImageUrl && (
                      <div className={`w-16 h-16 bg-slate-800 rounded-lg overflow-hidden flex-shrink-0 border ${isDark ? 'border-slate-600' : 'border-gray-300'}`}>
                        <img 
                          src={assetImageUrl} 
                          alt={repair.asset_name || 'Asset'} 
                          className="w-full h-full object-contain p-1"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-base font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {repair.asset_name || repair.asset_id}
                      </h3>
                      <p className={`text-sm mb-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                        {repair.title || 'Repair Request'}
                      </p>
                      <div className="flex items-center gap-4 text-xs">
                        <span className={`px-2 py-1 rounded ${
                          repair.priority === 'Critical' || repair.priority === 'High' ? 'bg-red-500/20 text-red-400' :
                          repair.priority === 'Medium' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {repair.priority || 'Medium'}
                        </span>
                        {repair.total_repair_cost && (
                          <span className={`${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                            ${parseFloat(repair.total_repair_cost).toFixed(2)}
                          </span>
                        )}
                        {repair.comment_count > 0 && (
                          <span className={`flex items-center gap-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                            <MessageSquare size={12} />
                            {repair.comment_count}
                          </span>
                        )}
                      </div>
                    </div>
                    <Info size={18} className={`flex-shrink-0 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Details Modal Component - shows asset details, images, and chat
const DetailsModal = ({ 
  ticket, 
  isDark, 
  repairFiles, 
  onClose,
  getRepairMessages,
  loadRepairFiles,
  moveRepair,
  isCustomer: isCustomerProp,
  isVendor: isVendorProp,
  setSummaryTicket,
  updatePriority
}) => {
  const { user } = useAuth();
  
  // Compute context-based assigned vendor/owner status from repair relationship
  const isAssignedVendorContextValue = isVendorProp !== undefined ? isVendorProp : isVendorContext(user, ticket);
  const isCustomerContextValue = isCustomerProp !== undefined ? isCustomerProp : isCustomerContext(user, ticket);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [showAttachedImagesModal, setShowAttachedImagesModal] = useState(false);
  const [showSummaryReport, setShowSummaryReport] = useState(false);
  const [isChecklistExpanded, setIsChecklistExpanded] = useState(false);
  const [isAssetInfoExpanded, setIsAssetInfoExpanded] = useState(true); // Expanded by default
  const [isAssignedVendorInspectionImagesExpanded, setIsAssignedVendorInspectionImagesExpanded] = useState(false);
  const [isChangingPriority, setIsChangingPriority] = useState(false);
  const [newPriority, setNewPriority] = useState(ticket?.priority || 'Medium');
  const assetImageUrl = ticket?.asset_image_url || 
    (ticket?.asset_type ? getAssetImageUrl(ticket.asset_type + '.png') : null);

  // Handlers for customer approval actions
  const handleApprove = async () => {
    try {
      await moveRepair(ticket.id, 'In Repair');
      if (setSummaryTicket) setSummaryTicket(prev => prev ? { ...prev, status: 'In Repair' } : null);
      onClose();
    } catch (error) {
      console.error('Error approving repair:', error);
      alert('Failed to approve repair. Please try again.');
    }
  };

  const handleDecline = async () => {
    if (window.confirm('Are you sure you want to decline this repair? It will be sent back to the assigned vendor for revision.')) {
      try {
        await moveRepair(ticket.id, 'Sent to Vendor');
        if (setSummaryTicket) setSummaryTicket(prev => prev ? { ...prev, status: 'Sent to Vendor' } : null);
        onClose();
      } catch (error) {
        console.error('Error declining repair:', error);
        alert('Failed to decline repair. Please try again.');
      }
    }
  };

  // Handlers for vendor completion actions
  const handleMarkComplete = async () => {
    try {
      await moveRepair(ticket.id, 'Complete');
      if (setSummaryTicket) setSummaryTicket(prev => prev ? { ...prev, status: 'Complete' } : null);
      onClose();
    } catch (error) {
      console.error('Error marking repair as complete:', error);
      alert('Failed to mark repair as complete. Please try again.');
    }
  };

  const handleScrap = async () => {
    if (window.confirm('Are you sure you want to scrap this repair? This action cannot be undone.')) {
      try {
        // Move to Complete with a note that it was scrapped
        await moveRepair(ticket.id, 'Complete');
        if (setSummaryTicket) setSummaryTicket(prev => prev ? { ...prev, status: 'Complete' } : null);
        alert('Repair marked as scrapped and moved to Complete.');
        onClose();
      } catch (error) {
        console.error('Error scrapping repair:', error);
        alert('Failed to scrap repair. Please try again.');
      }
    }
  };

  // Handler for priority change
  const handlePriorityChange = async () => {
    if (!updatePriority || newPriority === ticket.priority) {
      setIsChangingPriority(false);
      return;
    }
    
    try {
      await updatePriority(ticket.id, newPriority);
      setIsChangingPriority(false);
      if (setSummaryTicket) setSummaryTicket(prev => prev ? { ...prev, priority: newPriority } : null);
    } catch (error) {
      console.error('Error changing priority:', error);
      alert('Failed to change priority. Please try again.');
    }
  };

  // Parse repair details to get checklist
  const getRepairDetails = () => {
    const repairData = ticket?.repair_details || ticket?.damage_details;
    if (!repairData) return null;
    try {
      return typeof repairData === 'string' 
        ? JSON.parse(repairData) 
        : repairData;
    } catch {
      return null;
    }
  };

  const repairDetails = getRepairDetails();
  
  // Get vendor inspection images - from repair details or repair files
  const getVendorInspectionImages = () => {
    // First try from repair details
    if (repairDetails?.vendor_inspection_images && Array.isArray(repairDetails.vendor_inspection_images)) {
      return repairDetails.vendor_inspection_images;
    }
    // Then try from repair files (filtered by source)
    if (repairFiles && repairFiles[ticket.id]) {
      const vendorImages = repairFiles[ticket.id].filter(file => 
        file._source === 'vendor_inspection' || file.category === 'vendor_inspection'
      );
      return vendorImages.map(file => file.id);
    }
    return [];
  };

  const vendorInspectionImageIds = getVendorInspectionImages();
  
  // Get checklist - either from checklist array or build from items
  const getChecklist = () => {
    if (repairDetails?.checklist && Array.isArray(repairDetails.checklist)) {
      return repairDetails.checklist;
    }
    // If no checklist but we have items, try to build checklist from items
    if (repairDetails?.items && Array.isArray(repairDetails.items)) {
      return repairDetails.items.map(item => ({
        id: item.id,
        label: item.label,
        status: item.cost > 0 ? 'Damaged' : 'Good',
        cost: item.cost || 0
      }));
    }
    return null;
  };

  const checklist = getChecklist();

  // Load repair files when modal opens
  useEffect(() => {
    if (ticket?.id && loadRepairFiles) {
      console.log('DetailsModal: Loading files for repair', ticket.id);
      loadRepairFiles(ticket.id).catch(err => {
        console.error('DetailsModal: Error loading repair files:', err);
      });
    } else {
      console.log('DetailsModal: Missing ticket.id or loadRepairFiles', { 
        hasTicketId: !!ticket?.id, 
        hasLoadRepairFiles: !!loadRepairFiles 
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.id]); // Only depend on ticket.id to prevent infinite loops

  if (!ticket) return null;

  return (
    <>
    <div className={`flex flex-col h-full overflow-hidden`}>
      {/* Chat Thread */}
      <div className={`flex-1 min-h-0`}>
        <ChatThread
          repairId={ticket.id}
          isOpen={true}
          onClose={null}
          title="Chat"
          compact={true}
        />
      </div>

        {/* Content - Fixed Height, No Scroll */}
        <div className="shrink-0 p-3 flex flex-col">
            {/* Repair Checklist */}
            {checklist && checklist.length > 0 ? (
              <div className="flex flex-col">
                <div className={`grid grid-cols-2 gap-2.5`}>
                  {checklist.map((item, idx) => {
                    const isDamaged = item.status === 'Damaged';
                    const bgColor = isDamaged
                      ? isDark ? 'bg-red-500/15 border-red-500/40' : 'bg-red-50/80 border-red-200'
                      : isDark ? 'bg-emerald-500/15 border-emerald-500/40' : 'bg-emerald-50/80 border-emerald-200';
                    
                    return (
                      <div key={idx} className={`flex items-center justify-between p-2.5 rounded-lg border h-10 gap-2 ${bgColor}`}>
                        <span className={`text-sm font-medium break-words flex-1 ${isDark ? 'text-slate-100' : 'text-slate-800'}`}>
                          {item.label}
                        </span>
                        {isDamaged && (
                          <span className={`px-2 py-1 rounded text-xs font-semibold flex-shrink-0 ${isDark ? 'bg-slate-800/70 text-red-300 border border-red-500/40' : 'bg-white/80 text-red-700 border border-red-200'}`}>
                            ${parseFloat(item.cost || 0).toFixed(2)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Total Cost and Action Buttons */}
                <div className={`flex items-center justify-between mt-4 pt-4 border-t ${isDark ? 'border-slate-600' : 'border-gray-300'}`}>
                  {ticket.total_repair_cost && (
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Total Cost:</span>
                      <span className={`text-lg font-bold text-emerald-500`}>
                        ${parseFloat(ticket.total_repair_cost).toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    {isCustomerContextValue && ticket.status === 'Needs Approval' && (
                      <>
                        <button
                          onClick={handleApprove}
                          className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 text-sm"
                        >
                          <Check size={14} />
                          Approve
                        </button>
                        <button
                          onClick={handleDecline}
                          className="px-3 py-1.5 bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white border border-red-500/50 rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 text-sm"
                        >
                          <X size={14} />
                          Decline
                        </button>
                      </>
                    )}
                    {ticket.status === 'Complete' && (
                      <button
                        onClick={() => setShowSummaryReport(true)}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 text-sm"
                      >
                        <FileText size={14} />
                        Summary
                      </button>
                    )}
                    {isAssignedVendorContextValue && ticket.status === 'Needs Approval' && (
                      <button
                        onClick={async () => {
                          if (window.confirm('Start work from verbal approval? This will move the repair to In Repair status, but owner approval is still pending.')) {
                            try {
                              await moveRepair(ticket.id, 'In Repair');
                              if (setSummaryTicket) setSummaryTicket(prev => prev ? { ...prev, status: 'In Repair' } : null);
                              onClose();
                            } catch (error) {
                              console.error('Error starting work:', error);
                              alert('Failed to start work. Please try again.');
                            }
                          }
                        }}
                        className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 text-sm"
                      >
                        <Wrench size={14} />
                        Start Work
                      </button>
                    )}
                    {isAssignedVendorContextValue && ticket.status === 'In Repair' && (
                      <button
                        onClick={handleMarkComplete}
                        className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 text-sm"
                      >
                        <CheckCircle2 size={14} />
                        Complete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : null}

          {/* Vendor Inspection Images - Collapsible */}
          {vendorInspectionImageIds && vendorInspectionImageIds.length > 0 && (
            <div className={`border rounded-lg ${isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
              <button
                onClick={() => setIsAssignedVendorInspectionImagesExpanded(!isAssignedVendorInspectionImagesExpanded)}
                className={`w-full flex items-center justify-between p-4 hover:opacity-80 transition-opacity ${isDark ? 'hover:bg-slate-800/50' : 'hover:bg-gray-100'}`}
              >
                <div className="flex items-center gap-2">
                  <Image size={18} className={isDark ? 'text-purple-500' : 'text-purple-600'} />
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Vendor Inspection Images
                  </h3>
                  <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-slate-800 text-slate-400' : 'bg-gray-200 text-gray-600'}`}>
                    {vendorInspectionImageIds.length}
                  </span>
                </div>
                {isAssignedVendorInspectionImagesExpanded ? (
                  <ChevronUp size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
                ) : (
                  <ChevronDown size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
                )}
              </button>
              
              {isAssignedVendorInspectionImagesExpanded && (
                <div className={`px-4 pb-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                  <p className={`text-sm mb-3 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                    Images uploaded by vendor during inspection
                  </p>
                  <div className="grid grid-cols-3 gap-4">
                    {vendorInspectionImageIds.map((imageId, idx) => (
                      <AttachmentDisplay key={idx} fileId={imageId} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Inspection Images and Attached Images */}
          {(() => {
            const files = repairFiles && repairFiles[ticket.id] ? repairFiles[ticket.id] : [];
            const inspectionImages = files.filter(file => file._source === 'inspection');
            const regularImages = files.filter(file => file._source !== 'inspection');
            
            console.log('DetailsModal: Rendering images section', {
              repairId: ticket.id,
              totalFiles: files.length,
              inspectionImages: inspectionImages.length,
              regularImages: regularImages.length,
              repairFilesKeys: repairFiles ? Object.keys(repairFiles) : [],
              files: files.map(f => ({
                id: f.id,
                filename: f.original_filename || f.filename,
                source: f._source,
                hasViewUrl: !!(f.viewUrl || f.view_url || f.url)
              }))
            });
            
            // Show sections even if empty, or if there are any images
            if (inspectionImages.length === 0 && regularImages.length === 0) {
              console.log('DetailsModal: No images to display');
              return null; // Don't show anything if no images
            }
            
            return (
              <>
                {inspectionImages.length > 0 && (
                  <div>
                    <h3 className={`text-sm font-semibold mb-1.5 flex items-center gap-1.5 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      <Image size={14} />
                      Inspection Images ({inspectionImages.length})
                    </h3>
                    <div className="grid grid-cols-5 gap-1.5">
                      {inspectionImages.map((file, index) => {
                        let imageUrl = file.viewUrl || file.view_url || file.url;
                        if (imageUrl && imageUrl.startsWith('gs://')) {
                          imageUrl = null;
                        }
                        const filename = file.original_filename || file.filename || 'Image';
                        const isImage = file.mime_type?.startsWith('image/') || file.type?.startsWith('image/') || (imageUrl && !imageUrl.startsWith('gs://'));
                        
                        if (!isImage || !imageUrl) return null;
                        
                        return (
                          <div
                            key={file.id || `inspection-${index}`}
                            className={`border rounded overflow-hidden cursor-pointer hover:opacity-80 transition-opacity ${isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}
                            onClick={() => setLightboxImage({ url: imageUrl, filename })}
                          >
                            <img
                              src={imageUrl}
                              alt={filename}
                              className="w-full h-16 object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <div className={`p-0.5 ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
                              <p className={`text-[9px] truncate ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                                {filename}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              </>
            );
          })()}
        </div>

    </div>

      {/* Attached Images Modal */}
      {showAttachedImagesModal && (() => {
        const files = repairFiles && repairFiles[ticket.id] ? repairFiles[ticket.id] : [];
        const regularImages = files.filter(file => {
          if (file._source === 'inspection') return false;
          let imageUrl = file.viewUrl || file.view_url || file.url;
          if (imageUrl && imageUrl.startsWith('gs://')) return false;
          return file.mime_type?.startsWith('image/') || file.type?.startsWith('image/') || (imageUrl && !imageUrl.startsWith('gs://'));
        });
        
        return regularImages.length > 0 ? (
          <div 
            className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4"
            onClick={() => setShowAttachedImagesModal(false)}
          >
            <div 
              className={`max-w-4xl max-h-[90vh] overflow-y-auto ${isDark ? 'bg-slate-900' : 'bg-white'} rounded-lg p-6`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <Image size={20} />
                  Attached Images ({regularImages.length})
                </h3>
                <button
                  onClick={() => setShowAttachedImagesModal(false)}
                  className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-800' : 'hover:bg-gray-100'}`}
                >
                  <X size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
                </button>
              </div>
              <div className="grid grid-cols-4 gap-4">
                {regularImages.map((file, index) => {
                  let imageUrl = file.viewUrl || file.view_url || file.url;
                  if (imageUrl && imageUrl.startsWith('gs://')) {
                    imageUrl = null;
                  }
                  const filename = file.original_filename || file.filename || 'Image';
                  const isImage = file.mime_type?.startsWith('image/') || file.type?.startsWith('image/') || (imageUrl && !imageUrl.startsWith('gs://'));
                  
                  if (!isImage || !imageUrl) return null;
                  
                  return (
                    <div
                      key={file.id || `attachment-${index}`}
                      className={`border rounded overflow-hidden cursor-pointer hover:opacity-80 transition-opacity ${isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}
                      onClick={() => {
                        setShowAttachedImagesModal(false);
                        setLightboxImage({ url: imageUrl, filename });
                      }}
                    >
                      <img
                        src={imageUrl}
                        alt={filename}
                        className="w-full h-32 object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div className={`p-2 ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
                        <p className={`text-xs truncate ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                          {filename}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null;
      })()}

      {/* Repair Summary Report Modal */}
      {showSummaryReport && (
        <RepairSummaryReport
          ticket={ticket}
          onClose={() => setShowSummaryReport(false)}
          isDark={isDark}
        />
      )}

      {/* Lightbox for images */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X size={24} className="text-white" />
          </button>
          <img
            src={lightboxImage.url}
            alt={lightboxImage.filename}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

// Summary Panel Component - shared by Admin, Owner, and Assigned Vendor views
const SummaryPanel = ({ 
  ticket, 
  isCustomerView = false,
  isVendor: isVendorProp,
  isCustomer: isCustomerProp,
  isDark,
  moveRepair,
  finalizeRepair,
  updating,
  setSummaryTicket,
  setSelectedTicket,
  setShowVendorInspectionForm,
  setVendorInspectionTicket,
  setShowSummaryReport,
  repairFiles,
  getRepairMessages,
  loadRepairFiles,
  updatePriority
}) => {
  // Hooks must be called before any early returns
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const { user } = useAuth();
  
  if (!ticket) return null;
  
  // Compute context-based assigned vendor/owner status from repair relationship
  const isAssignedVendorContextValue = isVendorProp !== undefined ? isVendorProp : isVendorContext(user, ticket);
  const isCustomerContextValue = isCustomerProp !== undefined ? isCustomerProp : isCustomerContext(user, ticket);
  
  // For assigned vendors viewing "Needs Repair" or "Sent to Vendor" status, show special timeline with inspection step
  if (isAssignedVendorContextValue && (ticket.status === 'Needs Repair' || ticket.status === 'Sent to Vendor')) {
    return (
      <VendorInspectionTimeline 
        key={ticket.id} 
        ticket={ticket} 
        setShowVendorInspectionForm={setShowVendorInspectionForm}
        setVendorInspectionTicket={setVendorInspectionTicket}
        setSummaryTicket={setSummaryTicket}
        setSelectedTicket={setSelectedTicket}
        isDark={isDark}
      />
    );
  }
  
  // For vendors viewing "Needs Approval" status, show waiting timeline
  if (isAssignedVendorContextValue && ticket.status === 'Needs Approval') {
    return (
      <VendorWaitingForApprovalTimeline 
        key={ticket.id} 
        ticket={ticket} 
        setSummaryTicket={setSummaryTicket}
        setSelectedTicket={setSelectedTicket}
        isDark={isDark}
      />
    );
  }
  
  // For customers viewing "Needs Approval" status, show approval timeline
  if (isCustomerContextValue && ticket.status === 'Needs Approval') {
    return (
      <CustomerApprovalTimeline 
        key={ticket.id} 
        ticket={ticket} 
        moveRepair={moveRepair}
        setSummaryTicket={setSummaryTicket}
        setSelectedTicket={setSelectedTicket}
        isDark={isDark}
      />
    );
  }
  
  // For vendors viewing "In Repair" status, show completion timeline
  if (isAssignedVendorContextValue && ticket.status === 'In Repair') {
    return (
      <VendorCompletionTimeline 
        key={ticket.id} 
        ticket={ticket} 
        moveRepair={moveRepair}
        setSummaryTicket={setSummaryTicket}
        setSelectedTicket={setSelectedTicket}
        isDark={isDark}
      />
    );
  }
  
  // For customers viewing "In Repair" status, show waiting timeline
  if (isCustomerContextValue && ticket.status === 'In Repair') {
    return (
      <CustomerWaitingForCompletionTimeline 
        key={ticket.id} 
        ticket={ticket} 
        setSummaryTicket={setSummaryTicket}
        setSelectedTicket={setSelectedTicket}
        isDark={isDark}
      />
    );
  }
  
  // For Complete/Finalized status, show summary timeline for all roles
  if (ticket.status === 'Complete' || ticket.status === 'Finalized') {
    return (
      <CompleteSummaryTimeline
        key={ticket.id}
        ticket={ticket}
        setSummaryTicket={setSummaryTicket}
        setSelectedTicket={setSelectedTicket}
        finalizeRepair={finalizeRepair}
        updating={updating}
        isCustomer={isCustomerContextValue}
        isDark={isDark}
        setShowSummaryReport={setShowSummaryReport}
      />
    );
  }
  
  // Workflow module is vendor-only - no customer approval timeline should be shown
  // If user is not a vendor for this repair, show waiting timeline instead
  if (!isAssignedVendorContextValue && !isCustomerContextValue && ticket.status === 'Needs Approval') {
    return (
      <VendorWaitingForApprovalTimeline 
        key={ticket.id} 
        ticket={ticket} 
        setSummaryTicket={setSummaryTicket}
        setSelectedTicket={setSelectedTicket}
        isDark={isDark}
      />
    );
  }
  
  // Build history from status_history OR from individual timestamp columns
  let history = ticket.status_history || [];
  
  // If status_history is empty or incomplete, build it from timestamp columns
  if (!history || history.length === 0) {
    history = [];
    if (ticket.sent_to_vendor_at) {
      history.push({ status: 'Sent to Vendor', timestamp: ticket.sent_to_vendor_at });
    }
    if (ticket.needs_approval_at) {
      history.push({ status: 'Needs Approval', timestamp: ticket.needs_approval_at });
    }
    if (ticket.in_repair_at) {
      history.push({ status: 'In Repair', timestamp: ticket.in_repair_at });
    }
    if (ticket.completed_at) {
      history.push({ status: 'Complete', timestamp: ticket.completed_at });
    }
  }
  
  // Get asset image URL
  const assetImageUrl = ticket.asset_image_url || 
    (ticket.asset_type ? getAssetImageUrl(ticket.asset_type + '.png') : null);

  // Get status-specific colors for the panel
  const getStatusColors = () => {
    switch (ticket.status) {
      case 'Needs Repair':
      case 'Sent to Vendor':
        return {
          border: 'border-red-500/50',
          shadow: 'shadow-red-500/10',
          gradient: 'from-red-900/30',
          icon: 'text-red-400'
        };
      case 'Needs Approval':
        return {
          border: 'border-yellow-500/50',
          shadow: 'shadow-yellow-500/10',
          gradient: 'from-yellow-900/30',
          icon: 'text-yellow-400'
        };
      case 'In Repair':
        return {
          border: 'border-blue-500/50',
          shadow: 'shadow-blue-500/10',
          gradient: 'from-blue-900/30',
          icon: 'text-blue-400'
        };
      case 'Complete':
      case 'Finalized':
        return {
          border: 'border-emerald-500/50',
          shadow: 'shadow-emerald-500/10',
          gradient: 'from-emerald-900/30',
          icon: 'text-emerald-400'
        };
      default:
        return {
          border: 'border-slate-700',
          shadow: 'shadow-slate-900/10',
          gradient: 'from-slate-800/50',
          icon: 'text-slate-400'
        };
    }
  };

  const statusColors = getStatusColors();

  // Helper to get formatted date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  };

  // Check if customer is viewing "Sent to Vendor" or "Needs Repair" status - gray out future steps
  const { user: timelineUser } = useAuth();
  const isOwnerViewingSentToVendor = isCustomerContext(timelineUser, ticket) && (ticket.status === 'Sent to Vendor' || ticket.status === 'Needs Repair');

  return (
    <>
      <div className={`flex flex-col h-full rounded-xl border overflow-hidden ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 sm:p-6 border-b ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center gap-4">
            {assetImageUrl && (
              <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-slate-800 rounded-xl overflow-hidden flex-shrink-0 border-2 ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
                <img 
                  src={assetImageUrl} 
                  alt={ticket.asset_name || 'Asset'} 
                  className="w-full h-full object-contain p-2"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-white">{ticket.asset_name || ticket.asset_id}</h2>
              <p className="text-xs sm:text-sm text-slate-400 mt-1">
                {ticket.title || 'Repair Request'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setShowDetailsModal(true)}
              className={`p-1.5 hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-1.5 ${isDark ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              title="View Details"
            >
              <Info size={16} />
              <span className="text-xs font-medium hidden sm:inline">Details</span>
            </button>
            <button 
              onClick={() => { setSummaryTicket(null); setSelectedTicket(null); }}
              className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <X size={18} className="text-slate-400" />
            </button>
          </div>
        </div>

      {/* Timeline Content - Scrollable */}
      <div className="flex-1 overflow-y-auto py-6 sm:py-8 px-4 sm:px-8 hide-scrollbar">
        <div className="relative max-w-4xl mx-auto w-full">
          {/* Vertical Timeline Line */}
          <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-0.5 h-full bg-slate-700/50" />

          {/* Timeline Items */}
          <div className="space-y-8 w-full">
            {/* Step 1: Sent to Vendor */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className="absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-red-500 bg-red-500/20 z-10 mt-1.5 sm:mt-0" />
              <div className="w-full sm:w-5/12 ml-6 sm:ml-auto sm:pr-8 sm:text-right">
                <div className="rounded-xl p-4 sm:p-5 border-2 border-red-500/50 bg-red-500/10 shadow-lg">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3 sm:justify-end">
                    <Send size={18} className="text-red-400" />
                    <h3 className="text-base sm:text-lg font-bold text-red-400">Sent to Vendor</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-400">Request initiated</p>
                  {ticket.created_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      {formatDate(ticket.created_at)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Step 2: Needs Approval */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className={`absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 z-10 mt-1.5 sm:mt-0 ${isOwnerViewingSentToVendor ? 'border-slate-500 bg-slate-500/20' : 'border-yellow-500 bg-yellow-500/20'}`} />
              <div className="w-full sm:w-5/12 ml-6 sm:mr-auto sm:pl-8 sm:text-left">
                <div className={`rounded-xl p-4 sm:p-5 border-2 shadow-lg ${isOwnerViewingSentToVendor ? 'border-slate-500/50 bg-slate-500/10 opacity-50' : 'border-yellow-500 bg-yellow-500/20'}`}>
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <Clock size={18} className={isOwnerViewingSentToVendor ? 'text-slate-400' : 'text-yellow-400'} />
                    <h3 className={`text-base sm:text-lg font-bold ${isOwnerViewingSentToVendor ? 'text-slate-400' : 'text-yellow-400'}`}>Needs Approval</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-400">Waiting for approval</p>
                  {ticket.needs_approval_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      {formatDate(ticket.needs_approval_at)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Step 3: In Repair */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className={`absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 z-10 mt-1.5 sm:mt-0 ${isOwnerViewingSentToVendor ? 'border-slate-500 bg-slate-500/20' : 'border-blue-500 bg-blue-500/20'}`} />
              <div className="w-full sm:w-5/12 ml-6 sm:ml-auto sm:pr-8 sm:text-right">
                <div className={`rounded-xl p-4 sm:p-5 border-2 shadow-lg ${isOwnerViewingSentToVendor ? 'border-slate-500/50 bg-slate-500/10 opacity-50' : 'border-blue-500 bg-blue-500/20'}`}>
                  <div className="flex items-center gap-2 mb-2 sm:mb-3 sm:justify-end">
                    <Wrench size={18} className={isOwnerViewingSentToVendor ? 'text-slate-400' : 'text-blue-400'} />
                    <h3 className={`text-base sm:text-lg font-bold ${isOwnerViewingSentToVendor ? 'text-slate-400' : 'text-blue-400'}`}>In Repair</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-400">Repair in progress</p>
                  {ticket.in_repair_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      {formatDate(ticket.in_repair_at)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Step 4: Finalized */}
            <div className="relative flex items-start sm:items-center w-full">
              <div className={`absolute left-0 sm:left-1/2 transform sm:-translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 z-10 mt-1.5 sm:mt-0 ${isOwnerViewingSentToVendor ? 'border-slate-500 bg-slate-500/20' : 'border-emerald-500 bg-emerald-500/20'}`} />
              <div className="w-full sm:w-5/12 ml-6 sm:mr-auto sm:pl-8 sm:text-left">
                <div className={`rounded-xl p-4 sm:p-5 border-2 shadow-lg ${isOwnerViewingSentToVendor ? 'border-slate-500/50 bg-slate-500/10 opacity-50' : 'border-emerald-500 bg-emerald-500/20'}`}>
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <CheckCircle2 size={18} className={isOwnerViewingSentToVendor ? 'text-slate-400' : 'text-emerald-400'} />
                    <h3 className={`text-base sm:text-lg font-bold ${isOwnerViewingSentToVendor ? 'text-slate-400' : 'text-emerald-400'}`}>Finalized</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-400">Repair closed and archived</p>
                  {ticket.completed_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      {formatDate(ticket.completed_at)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

      {/* Details Modal */}
      {showDetailsModal && (
        <DetailsModal
          ticket={ticket}
          isVendor={isAssignedVendorContextValue}
          isCustomer={isCustomerContextValue}
          isDark={isDark}
          repairFiles={repairFiles}
          getRepairMessages={getRepairMessages}
          loadRepairFiles={loadRepairFiles}
          moveRepair={moveRepair}
          setSummaryTicket={setSummaryTicket}
          updatePriority={updatePriority}
          onClose={() => setShowDetailsModal(false)}
        />
      )}
    </>
  );
};

export default function Workflows() {
  console.log('📋 [Workflows Page] Component mounted');
  
  const navigate = useNavigate();
  const location = useLocation();
  const { repairs: initialRepairs, warehouses, organizations = [], loading, createRepair, updateRepair, archiveRepair, unarchiveRepair, getRepairComments, addRepairComment, deleteRepairComment, getRepairMessages } = useApi();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const { isAdmin } = usePermissions();
  
  console.log('📋 [Workflows Page] State:', {
    hasUser: !!user,
    userRole: user?.role,
    isDeveloper: user?.isDeveloper,
    repairsCount: initialRepairs?.length || 0,
    warehousesCount: Object.keys(warehouses || {}).length,
    loading
  });
  const [repairs, setRepairs] = useState([]);
  const [updating, setUpdating] = useState({});
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // WORKFLOWS MODULE: Always use vendor view (no toggle)
  const viewMode = 'vendor';
  // View mode toggle: 'kanban' (default) or 'list'
  const [repairViewMode, setRepairViewMode] = useState('kanban');
  // Kanban status filter tile ('all' or a specific status id)
  const [kanbanStatusFilter, setKanbanStatusFilter] = useState('all');
  // List view status filter tile ('all' or a specific status id)
  const [listStatusFilter, setListStatusFilter] = useState('all');
  const [editingRepairId, setEditingRepairId] = useState(null); // For editing existing repair (vendor inspection)
  const [selectedTicket, setSelectedTicket] = useState(null); // For viewing ticket details/comments
  const [summaryTicket, setSummaryTicket] = useState(null); // For the Summary panel
  const [selectedConversationId, setSelectedConversationId] = useState(null); // For vendor conversation panel
  const [showSummaryReport, setShowSummaryReport] = useState(null); // For the Repair Summary Report modal
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [newTicketForm, setNewTicketForm] = useState({
    assetId: '',
    title: '',
    description: '',
    priority: 'Medium',
    status: 'Needs Approval', // Default: goes directly to vendor for approval
    notes: '',
    totalRepairCost: '',
    damageDetails: {
      type: '',
      location: '',
      description: ''
    }
  });
  const [selectedAssetDetails, setSelectedAssetDetails] = useState(null);
  const [repairChecklist, setRepairChecklist] = useState(() => {
    const initial = {};
    REPAIR_ITEMS.forEach(item => { initial[item.id] = 'Good'; });
    return initial;
  });
  const [otherDamage, setOtherDamage] = useState({ description: '', cost: '' });
  const [attachments, setAttachments] = useState([]);
  const attachmentInputRef = useRef(null);
  const [repairFiles, setRepairFiles] = useState({}); // Map repair ID to files
  const [showVendorInspectionForm, setShowVendorInspectionForm] = useState(false); // Track if vendor is filling out inspection
  const [vendorInspectionTicket, setVendorInspectionTicket] = useState(null); // Ticket being inspected in modal
  const [columnDetailsModal, setColumnDetailsModal] = useState(null); // { columnId, columnTitle, repairs } for column details modal
  const [selectedRepairForDetails, setSelectedRepairForDetails] = useState(null); // Selected repair for details modal
  const [selectedNeedsRepairCard, setSelectedNeedsRepairCard] = useState(null); // Highlighted "Needs Repair" card (shows Inspection button)
  const [showScanner, setShowScanner] = useState(false); // Track if PLX scanner is open
  
  // Template selection for New Ticket Modal
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [selectedNewTicketTemplate, setSelectedNewTicketTemplate] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [useTemplate, setUseTemplate] = useState(false); // Toggle between manual and template
  
  // Filter state
  const [vendorFilter, setVendorFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [searchFilter, setSearchFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('all');

  // Get all assets for the dropdown
  const allAssets = useMemo(() => {
    return Object.values(warehouses).flatMap(w => w.assets || []);
  }, [warehouses]);

  // Quick lookup for asset -> customer/brand/name info
  const assetLookup = useMemo(() => {
    const orgMap = {};
    (organizations || []).forEach(org => {
      orgMap[org.id] = org.name || org.displayName || org.organization_name || org.title;
    });

    const map = {};
    Object.values(warehouses).forEach(wh => {
      const customerName = wh.displayName || wh.name || orgMap[wh.org_id] || 'Customer';
      (wh.assets || []).forEach(asset => {
        map[asset.id] = {
          assetName: asset.name || asset.assetType || asset.type || 'Asset',
          assetNumber: asset.id,
          brand: asset.brand || asset.type || asset.assetType || asset.category || 'N/A',
          customerName,
          warehouseId: wh.id,
        };
      });
    });
    return map;
  }, [warehouses, organizations]);

  // Auto-calc total from checklist selections
  const computedRepairTotal = useMemo(() => {
    let total = 0;
    
    // Use template items or default REPAIR_ITEMS (only if template toggle is on)
    const items = (useTemplate && selectedNewTicketTemplate) ? selectedNewTicketTemplate.checklist_items : REPAIR_ITEMS;
    
    items.forEach(item => {
      if (repairChecklist[item.id] === 'Damaged') {
        total += (item.cost || 0); // Templates use 'cost', default items also use 'cost'
      }
    });
    
    if (otherDamage.cost) {
      const extra = parseFloat(otherDamage.cost);
      if (!Number.isNaN(extra)) total += extra;
    }
    return total;
  }, [repairChecklist, otherDamage, selectedNewTicketTemplate, useTemplate]);

  // Filter out finalized repairs from the workflow
  const activeRepairs = useMemo(() => {
    return repairs.filter(r => r.status !== 'Finalized');
  }, [repairs]);

  // Get unique vendors and companies for filters
  const uniqueVendors = useMemo(() => {
    const vendors = new Set();
    repairs.forEach(repair => {
      if (repair.vendor_email) vendors.add(repair.vendor_email);
      if (repair.vendor_company_name) vendors.add(repair.vendor_company_name);
    });
    return Array.from(vendors).sort();
  }, [repairs]);

  const uniqueCompanies = useMemo(() => {
    const companies = new Set();
    repairs.forEach(repair => {
      if (repair.company_name) companies.add(repair.company_name);
    });
    return Array.from(companies).sort();
  }, [repairs]);

  // Apply filters to repairs
  const filteredRepairs = useMemo(() => {
    let filtered = activeRepairs;

    // Vendor filter (for customers)
    if (vendorFilter !== 'all') {
      filtered = filtered.filter(r => 
        r.vendor_email === vendorFilter || r.vendor_company_name === vendorFilter
      );
    }

    // Company filter (for vendors)
    if (companyFilter !== 'all') {
      filtered = filtered.filter(r => r.company_name === companyFilter);
    }

    // Customer filter (for vendors) - searchable dropdown
    if (customerFilter !== 'all') {
      filtered = filtered.filter(r => r.company_name === customerFilter);
    }

    // Search filter (by asset name, ID, or type)
    if (searchFilter.trim()) {
      const search = searchFilter.toLowerCase();
      filtered = filtered.filter(r => 
        (r.asset_name && r.asset_name.toLowerCase().includes(search)) ||
        (r.asset_id && r.asset_id.toLowerCase().includes(search)) ||
        (r.asset_type && r.asset_type.toLowerCase().includes(search))
      );
    }

    return filtered;
  }, [activeRepairs, vendorFilter, companyFilter, customerFilter, searchFilter]);

  // Calculate totals for customer view
  const customerRepairTotals = useMemo(() => {
    // Total for assets in repair and complete
    const inRepairAndComplete = activeRepairs.filter(r => 
      r.status === 'In Repair' || r.status === 'Complete'
    );
    const totalInRepairAndComplete = inRepairAndComplete.reduce((sum, r) => {
      return sum + (parseFloat(r.total_repair_cost) || 0);
    }, 0);

    // Total for assets not approved (Needs Approval status)
    const needsApproval = activeRepairs.filter(r => r.status === 'Needs Approval');
    const totalNeedsApproval = needsApproval.reduce((sum, r) => {
      return sum + (parseFloat(r.total_repair_cost) || 0);
    }, 0);

    return {
      totalInRepairAndComplete,
      totalNeedsApproval
    };
  }, [activeRepairs]);

  // Handle form field changes
  const handleFormChange = (field, value) => {
    if (field === 'assetId') {
      const details = assetLookup[value];
      setSelectedAssetDetails(details || null);
      setNewTicketForm(prev => ({
        ...prev,
        [field]: value,
        // Pre-fill a helpful title when none exists
        ...(details && !prev.title ? { title: `Repair - ${details.assetName}` } : {})
      }));
      return;
    }
    setNewTicketForm(prev => ({ ...prev, [field]: value }));
  };

  // Handle damage details changes
  const handleDamageDetailsChange = (field, value) => {
    setNewTicketForm(prev => ({
      ...prev,
      damageDetails: { ...prev.damageDetails, [field]: value }
    }));
  };

  // Toggle checklist item between Good/Damaged
  const toggleRepairItem = (itemId) => {
    setRepairChecklist(prev => ({
      ...prev,
      [itemId]: prev[itemId] === 'Damaged' ? 'Good' : 'Damaged'
    }));
  };

  const resetRepairForm = () => {
    setNewTicketForm({
      assetId: '',
      title: '',
      description: '',
      priority: 'Medium',
      status: 'Needs Approval',
      notes: '',
      totalRepairCost: '',
      damageDetails: {
        type: '',
        location: '',
        description: ''
      }
    });
    setSelectedAssetDetails(null);
    setSelectedNewTicketTemplate(null);
    setAvailableTemplates([]);
    setUseTemplate(false);
    setRepairChecklist(() => {
      const reset = {};
      REPAIR_ITEMS.forEach(item => { reset[item.id] = 'Good'; });
      return reset;
    });
    setOtherDamage({ description: '', cost: '' });
    setAttachments([]);
  };

  // Handle template selection for New Ticket
  const handleNewTicketTemplateSelect = (template) => {
    if (!template) {
      setSelectedNewTicketTemplate(null);
      // Reset to default REPAIR_ITEMS
      setRepairChecklist(() => {
        const reset = {};
        REPAIR_ITEMS.forEach(item => { reset[item.id] = 'Good'; });
        return reset;
      });
      return;
    }

    setSelectedNewTicketTemplate(template);

    // Replace repair checklist with template items (all default to 'Good')
    if (template.checklist_items && Array.isArray(template.checklist_items)) {
      const newChecklist = {};
      template.checklist_items.forEach(item => {
        newChecklist[item.id] = 'Good';
      });
      setRepairChecklist(newChecklist);
    }
  };

  // Handle PLX code scan
  const handlePLXScan = (scannedId) => {
    console.log('PLX scanned:', scannedId);
    setShowScanner(false);
    
    // Try to find asset by the scanned ID
    // The scanned ID is the numeric portion, so we need to find the matching asset
    const matchedAsset = allAssets.find(asset => {
      // Check if asset.id contains the scanned number
      // Could be exact match like "1000" or formatted like "G2-1000"
      if (asset.id === scannedId) return true;
      
      // Extract numeric part from asset ID
      const numericMatch = asset.id.match(/\d+/);
      if (numericMatch && numericMatch[0] === scannedId) return true;
      
      // Check fiducial_marker_id
      if (asset.fiducial_marker_id && String(asset.fiducial_marker_id) === scannedId) return true;
      
      return false;
    });
    
    if (matchedAsset) {
      // Auto-fill the asset
      handleFormChange('assetId', matchedAsset.id);
    } else {
      alert(`No asset found with PLX code: ${scannedId}`);
    }
  };

  // Load comments for a ticket
  const loadComments = async (repairId) => {
    if (!getRepairComments) return;
    setLoadingComments(true);
    try {
      const commentsData = await getRepairComments(repairId);
      setComments(commentsData || []);
    } catch (error) {
      console.error('Error loading comments:', error);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  // Helper function to open new ticket modal with a ticket pre-filled (for Needs Repair inspection)
  const openInspectionForm = (ticket) => {
    // Set editing repair ID so it updates the existing repair
    setEditingRepairId(ticket.id);
    
    // Pre-fill the asset - use the same logic as VendorInspectionForm
    // VendorInspectionForm does: const assetInfo = assetLookup[ticket.asset_id] || {};
    // and then sets: setLocalAssetDetails(assetInfo);
    // So we should do the same - get assetInfo from lookup, and if empty, build it from ticket data
    if (ticket.asset_id) {
      // First try assetLookup (same as VendorInspectionForm)
      let assetInfo = assetLookup[ticket.asset_id] || {};
      
      // If assetInfo is empty, build it from ticket data (same fallback logic)
      if (!assetInfo || Object.keys(assetInfo).length === 0) {
        assetInfo = {
          assetName: ticket.asset_name || ticket.asset_type || ticket.assetType || 'Asset',
          assetNumber: ticket.asset_id,
          brand: ticket.asset_type || ticket.assetType || 'N/A',
          customerName: ticket.company_name || 'Customer',
          warehouseId: null
        };
      }
      
      // Set the asset ID in the form
      setNewTicketForm(prev => ({
        ...prev,
        assetId: ticket.asset_id
      }));
      
      // Set selectedAssetDetails directly (same as VendorInspectionForm sets localAssetDetails)
      setSelectedAssetDetails({
        id: ticket.asset_id,
        name: assetInfo.assetName,
        assetName: assetInfo.assetName,
        assetNumber: assetInfo.assetNumber || ticket.asset_id,
        assetType: assetInfo.brand || ticket.asset_type || 'Unknown',
        type: assetInfo.brand || ticket.asset_type || 'Unknown',
        brand: assetInfo.brand || 'N/A',
        customerName: assetInfo.customerName || ticket.company_name || 'Customer',
        warehouseId: assetInfo.warehouseId || null
      });
    }
    
    // Pre-fill form fields from existing ticket if available
    setNewTicketForm(prev => ({
      ...prev,
      title: ticket.title || prev.title,
      description: ticket.description || prev.description,
      notes: ticket.notes || prev.notes,
      priority: ticket.priority || prev.priority
    }));
    
    // Open the new ticket modal
    setShowNewTicketModal(true);
  };

  // Handle opening ticket details - opens details modal
  const handleTicketClick = async (ticket) => {
    // If ticket is in "Needs Repair" status and user is vendor, highlight the card first
    // (shows an "Inspection" button — clicking that opens the form)
    if (ticket.status === 'Needs Repair' && effectiveIsAssignedVendor) {
      setSelectedNeedsRepairCard(prev => prev?.id === ticket.id ? null : ticket);
      setSelectedRepairForDetails(null); // clear any other selection
      if (repairViewMode === 'kanban') setKanbanStatusFilter(ticket.status);
      return;
    }
    
    // Normalize damage_details to ensure it's an object and checklist is an array
    const normalizedTicket = {
      ...ticket,
      damage_details: ticket.damage_details 
        ? (typeof ticket.damage_details === 'string' 
            ? JSON.parse(ticket.damage_details) 
            : ticket.damage_details)
        : {}
    };
    // Ensure checklist is an array if it exists
    if (normalizedTicket.damage_details.checklist && !Array.isArray(normalizedTicket.damage_details.checklist)) {
      normalizedTicket.damage_details.checklist = [];
    }
    // Open details modal and load related data
    setSelectedRepairForDetails(normalizedTicket);
    setSelectedNeedsRepairCard(null); // Clear any Needs Repair highlight
    setSummaryTicket(null); // Clear summary panel
    setSelectedTicket(null);
    setShowVendorInspectionForm(false); // Reset form state
    setLightboxImage(null);
    // Kanban: sync the status filter tile to match the clicked card's column
    if (repairViewMode === 'kanban') {
      setKanbanStatusFilter(ticket.status);
    }
    // Load files for the selected repair
    await loadRepairFiles(ticket.id);
    loadComments(ticket.id);
    loadRepairFiles(ticket.id);
  };

  // Handle adding a comment
  const handleAddComment = async () => {
    if ((!newComment.trim() && commentAttachments.length === 0) || !selectedTicket || !addRepairComment) return;
    try {
      const comment = await addRepairComment(selectedTicket.id, newComment.trim() || '(Image)', commentAttachments);
      setComments(prev => [...prev, comment]);
      setNewComment('');
      setCommentAttachments([]);
      // Reload files to include new attachments
      loadRepairFiles(selectedTicket.id);
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment. Please try again.');
    }
  };

  // Handle deleting a comment
  const handleDeleteComment = async (commentId) => {
    if (!selectedTicket || !deleteRepairComment) return;
    try {
      await deleteRepairComment(selectedTicket.id, commentId);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert('Failed to delete comment. Please try again.');
    }
  };

  // Handle file attachment selection
  const handleAttachmentSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachments(prev => [...prev, ...files]);
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  };

  // Remove attachment
  const handleRemoveAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Load files for a repair (including attachments from messages and comments)
  const loadRepairFiles = useCallback(async (repairId) => {
    if (!repairId) {
      console.warn('loadRepairFiles: No repairId provided');
      return;
    }
    console.log('loadRepairFiles: Starting to load files for repair', repairId);
    try {
      const api = (await import('../../../lib/api')).default;
      
      // Load files from media_files table
      const filesResponse = await api.get(`/api/files?repairId=${repairId}`);
      const mediaFiles = Array.isArray(filesResponse) ? filesResponse : (filesResponse?.files || []);
      
      // Load messages and extract attachments
      let messageAttachments = [];
      try {
        const messages = await getRepairMessages(repairId);
        if (Array.isArray(messages)) {
          messages.forEach(msg => {
            if (msg.attachments) {
              const attachments = typeof msg.attachments === 'string' ? JSON.parse(msg.attachments) : msg.attachments;
              if (Array.isArray(attachments)) {
                messageAttachments = messageAttachments.concat(attachments);
              }
            }
          });
        }
      } catch (err) {
        console.warn('Error loading messages for attachments:', err);
      }
      
      // Load comments and extract attachments
      let commentAttachments = [];
      try {
        const comments = await getRepairComments(repairId);
        if (Array.isArray(comments)) {
          comments.forEach(comment => {
            if (comment.attachments) {
              const attachments = typeof comment.attachments === 'string' ? JSON.parse(comment.attachments) : comment.attachments;
              if (Array.isArray(attachments)) {
                commentAttachments = commentAttachments.concat(attachments);
              }
            }
          });
        }
      } catch (err) {
        console.warn('Error loading comments for attachments:', err);
      }
      
      // Load inspection images for this repair
      let inspectionImages = [];
      try {
        const inspectionsResponse = await api.get(`/api/inspections?repairId=${repairId}`);
        const inspections = Array.isArray(inspectionsResponse) ? inspectionsResponse : [];
        
        // Get all image file IDs from inspections
        const imageFileIds = [];
        inspections.forEach(inspection => {
          if (inspection.images && Array.isArray(inspection.images)) {
            imageFileIds.push(...inspection.images);
          }
        });
        
        // Fetch file details for each image ID
        if (imageFileIds.length > 0) {
          const filePromises = imageFileIds.map(fileId => 
            api.get(`/api/files/${fileId}`).catch(err => {
              console.warn(`Error loading inspection image ${fileId}:`, err);
              return null;
            })
          );
          const fileResults = await Promise.all(filePromises);
          inspectionImages = fileResults
            .filter(file => file !== null && file.mime_type?.startsWith('image/'))
            .map(file => ({
              ...file,
              _source: 'inspection' // Mark as inspection image
            }));
        }
      } catch (err) {
        console.warn('Error loading inspection images:', err);
      }
      
      // Combine all images
      const allImages = [
        ...mediaFiles.filter(f => f.mime_type?.startsWith('image/')),
        ...messageAttachments.filter(a => {
          const url = a.url;
          return (a.type?.startsWith('image/') || url) && url && !url.startsWith('gs://');
        }),
        ...commentAttachments.filter(a => {
          const url = a.url;
          return (a.type?.startsWith('image/') || url) && url && !url.startsWith('gs://');
        }),
        ...inspectionImages
      ];
      
      console.log(`✅ loadRepairFiles: Loaded ${allImages.length} images for repair ${repairId}`, {
        mediaFiles: mediaFiles.length,
        messageAttachments: messageAttachments.length,
        commentAttachments: commentAttachments.length,
        inspectionImages: inspectionImages.length,
        allImages: allImages.map(img => ({
          id: img.id,
          filename: img.original_filename || img.filename,
          hasViewUrl: !!(img.viewUrl || img.view_url),
          source: img._source || 'regular'
        }))
      });
      
      setRepairFiles(prev => {
        const updated = { ...prev, [repairId]: allImages };
        console.log('loadRepairFiles: Updated repairFiles state', { 
          repairId, 
          imageCount: allImages.length,
          allRepairIds: Object.keys(updated)
        });
        return updated;
      });
    } catch (error) {
      console.error('❌ loadRepairFiles: Error loading repair files:', error);
      setRepairFiles(prev => ({ ...prev, [repairId]: [] }));
    }
    }, [getRepairMessages, getRepairComments]);

  // Handle form submission (create new or update existing)
  const handleCreateTicket = async (e) => {
    e.preventDefault();
    
    if (!newTicketForm.assetId || !newTicketForm.title) {
      alert('Asset and Title are required.');
      return;
    }

    if (!createRepair && !editingRepairId) {
      console.error('createRepair function is not available');
      alert('Ticket creation is not available. Please refresh the page.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Use template items or default REPAIR_ITEMS
      const items = selectedNewTicketTemplate ? selectedNewTicketTemplate.checklist_items : REPAIR_ITEMS;
      
      const damageDetailsPayload = {
        ...newTicketForm.damageDetails,
        checklist: items.map(item => ({
          id: item.id,
          label: item.label,
          status: repairChecklist[item.id] || (item.options?.[0] || 'Good'),
          cost: item.default_cost || item.cost || 0
        })),
        ...(otherDamage.description || otherDamage.cost ? {
          otherDamage: {
            description: otherDamage.description,
            cost: otherDamage.cost ? parseFloat(otherDamage.cost) : null
          }
        } : {}),
        ...(selectedAssetDetails ? { assetMeta: selectedAssetDetails } : {})
      };

      const totalCostToSubmit = computedRepairTotal > 0
        ? parseFloat(computedRepairTotal.toFixed(2))
        : (newTicketForm.totalRepairCost ? parseFloat(newTicketForm.totalRepairCost) : null);

      let result;
      
      if (editingRepairId) {
        // Update existing repair and move to "Needs Approval"
        console.log('Updating repair ticket with inspection data:', editingRepairId);
        result = await updateRepair(editingRepairId, {
          title: newTicketForm.title,
          description: newTicketForm.description,
          priority: newTicketForm.priority,
          status: 'Needs Approval', // Move to needs approval after vendor fills it out
          notes: newTicketForm.notes || null,
          totalRepairCost: Number.isFinite(totalCostToSubmit) ? totalCostToSubmit : null,
          damageDetails: damageDetailsPayload,
          inspectionTemplateId: (useTemplate && selectedNewTicketTemplate) ? selectedNewTicketTemplate.id : null
        });
        console.log('Repair ticket updated successfully:', result);
      } else {
        // Create new repair
        console.log('Creating repair ticket with data:', newTicketForm);
        result = await createRepair({
          assetId: newTicketForm.assetId,
          title: newTicketForm.title,
          description: newTicketForm.description,
          priority: newTicketForm.priority,
          status: newTicketForm.status,
          notes: newTicketForm.notes || null,
          totalRepairCost: Number.isFinite(totalCostToSubmit) ? totalCostToSubmit : null,
          damageDetails: damageDetailsPayload,
          companyId: selectedAssetDetails?.companyId || null,
          inspectionTemplateId: (useTemplate && selectedNewTicketTemplate) ? selectedNewTicketTemplate.id : null
        });
        console.log('Repair ticket created successfully:', result);
      }

      // Upload attachments if any
      const repairId = result?.id || editingRepairId;
      if (attachments.length > 0 && repairId) {
        try {
          const api = (await import('../../../lib/api')).default;
          const formData = new FormData();
          attachments.forEach(file => {
            formData.append('files', file);
          });
          formData.append('repairId', repairId);
          if (newTicketForm.assetId) {
            formData.append('assetId', newTicketForm.assetId);
          }
          
          await api.upload('/api/files/upload', formData);
        } catch (uploadError) {
          console.error('Error uploading attachments:', uploadError);
          // Don't fail the whole operation if attachments fail
        }
      }

      // Reset form and close modal
      resetRepairForm();
      setEditingRepairId(null);
      setShowNewTicketModal(false);
    } catch (error) {
      console.error('Error creating repair ticket:', error);
      alert(error.message || 'Failed to create ticket. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Sync repairs from hook to local state
  useEffect(() => {
    if (initialRepairs) {
      setRepairs(initialRepairs);
    }
  }, [initialRepairs]);

  // Update summaryTicket when repairs change
  useEffect(() => {
    if (summaryTicket) {
      const updated = repairs.find(r => r.id === summaryTicket.id);
      if (updated && updated.status !== summaryTicket.status) {
        console.log(`🔄 Auto-updating summaryTicket: ${summaryTicket.status} -> ${updated.status}`);
        setSummaryTicket(updated);
      }
    }
  }, [repairs, summaryTicket]);

  // Load templates when New Ticket Modal opens and asset is selected
  useEffect(() => {
    const loadTemplatesForAsset = async () => {
      if (!showNewTicketModal || !newTicketForm.assetId) {
        setAvailableTemplates([]);
        setSelectedNewTicketTemplate(null);
        return;
      }

      try {
        setLoadingTemplates(true);
        // Get asset type from selected asset
        const selectedAsset = allAssets.find(a => a.id === newTicketForm.assetId);
        const assetType = selectedAsset?.type || selectedAsset?.assetType;

        // Load all templates (will filter by asset type on backend if needed)
        const response = await api.get('/api/inspection-templates');
        const allTemplates = response;

        // Filter templates that apply to this asset type
        const applicableTemplates = allTemplates.filter(t => {
          if (!t.is_active) return false;
          if (!t.asset_types || t.asset_types.length === 0) return true; // No filter = applies to all
          return assetType && t.asset_types.includes(assetType);
        });

        setAvailableTemplates(applicableTemplates);

        // Auto-select default template if available
        const defaultTemplate = applicableTemplates.find(t => t.is_default);
        if (defaultTemplate) {
          handleNewTicketTemplateSelect(defaultTemplate);
        }
      } catch (error) {
        console.error('Failed to load templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };

    loadTemplatesForAsset();
  }, [showNewTicketModal, newTicketForm.assetId, allAssets]);

  // Disabled automatic conversation loading to prevent resource exhaustion
  // Vendors can still access chats via the chat toggle button on each repair card
  // useEffect(() => {
  //   loadConversations();
  // }, [loadConversations]);

  // Disabled polling to prevent resource exhaustion - conversations are accessible via chat toggle
  // useEffect(() => {
  //   if (!isVendor || conversationRepairs.length === 0 || !getRepairUnreadCount) return;
  //   
  //   const pollUnreadCounts = async () => {
  //     const unreadCounts = {};
  //     for (const repair of conversationRepairs) {
  //       try {
  //         const count = await getRepairUnreadCount(repair.id);
  //         unreadCounts[repair.id] = count || 0;
  //       } catch (err) {
  //         unreadCounts[repair.id] = 0;
  //       }
  //     }
  //     setConversationUnreadCounts(unreadCounts);
  //   };
  //   
  //   // Only poll after initial load, don't run immediately
  //   const interval = setInterval(pollUnreadCounts, 30000); // 30 seconds
  //   return () => clearInterval(interval);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isVendor, conversationRepairs.length]); // Only re-run when vendor status or count changes

  // Disabled - Refresh unread count when a conversation is selected (after viewing messages)
  // useEffect(() => {
  //   if (!isVendor || !selectedConversationId || !getRepairUnreadCount) return;
  //   
  //   const refreshUnreadCount = async () => {
  //     try {
  //       const count = await getRepairUnreadCount(selectedConversationId);
  //       setConversationUnreadCounts(prev => ({
  //         ...prev,
  //         [selectedConversationId]: count || 0
  //       }));
  //     } catch (err) {
  //       // Silently handle errors to prevent console spam
  //     }
  //   };
  //   
  //   // Refresh after a short delay to allow messages to be marked as read
  //   const timeout = setTimeout(refreshUnreadCount, 1000);
  //   return () => clearTimeout(timeout);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isVendor, selectedConversationId]); // Only re-run when conversation changes

  // Disabled periodic refresh to prevent resource exhaustion
  // useEffect(() => {
  //   if (!isVendor) return;
  //   
  //   const interval = setInterval(() => {
  //     loadConversations();
  //   }, 60000); // Refresh every minute
  //   
  //   return () => clearInterval(interval);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isVendor]); // loadConversations is stable, repairs are handled in its own effect

  // Use viewMode toggle to determine view, but respect actual role for permissions
  const effectiveIsAssignedVendor = viewMode === 'vendor';
  const effectiveIsCustomer = viewMode === 'customer';
  const assignedVendorView = effectiveIsAssignedVendor || (user?.role === 'user' || isAdmin);

  // Convenience aliases used by nested components (VendorInspectionForm, etc.)
  // Workflows module is always vendor-side, so isVendor = true and isCustomer = false.
  const isVendor = effectiveIsAssignedVendor;   // true in Workflows
  const isCustomer = effectiveIsCustomer;        // false in Workflows

  // Columns differ based on role - Assigned vendors see "Needs Repair" instead of "Sent to Vendor"
  const columns = useMemo(() => {
    const allColumns = [
      { id: assignedVendorView ? 'Needs Repair' : 'Sent to Vendor', title: assignedVendorView ? 'Needs Repair' : 'Sent to Vendor', color: 'border-red-500/50', icon: Send },
      { id: 'Needs Approval', title: 'Needs Approval', color: 'border-yellow-500/50', icon: Clock },
      { id: 'In Repair', title: 'In Repair', color: 'border-blue-500/50', icon: Package },
      { id: 'Complete', title: 'Complete', color: 'border-emerald-500/50', icon: Check },
    ];
    
    // Assigned vendors see "Needs Repair" column, customers see "Sent to Vendor" column
    return allColumns;
  }, [assignedVendorView]);

  const moveRepair = async (repairId, newStatus, delayOptimisticUpdate = 0) => {
    console.log(`📦 moveRepair called: repairId=${repairId}, newStatus=${newStatus}, delay=${delayOptimisticUpdate}ms`);
    setUpdating(prev => ({ ...prev, [repairId]: true }));
    
    // Get the current repair to update status history (for optimistic UI update)
    const currentRepair = repairs.find(r => r.id === repairId);
    const now = new Date().toISOString();
    
    // Build new status history for optimistic UI
    const currentHistory = currentRepair?.status_history || [];
    const newHistory = [...currentHistory, { status: newStatus, timestamp: now }];
    
    // Function to perform the optimistic update
    const performOptimisticUpdate = () => {
      console.log(`🔄 moveRepair: Performing optimistic update NOW for repairId=${repairId}, newStatus=${newStatus}`);
      setRepairs(prev => prev.map(r => 
        r.id === repairId ? { 
          ...r, 
          status: newStatus, 
          status_history: newHistory,
          updated_at: now 
        } : r
      ));
    };
    
    // If delay is specified, wait before doing optimistic update (for animations)
    if (delayOptimisticUpdate > 0) {
      console.log(`⏳ moveRepair: Scheduling optimistic update in ${delayOptimisticUpdate}ms`);
      setTimeout(() => {
        console.log(`⏰ moveRepair: Delay completed, executing optimistic update`);
        performOptimisticUpdate();
      }, delayOptimisticUpdate);
    } else {
      console.log(`⚡ moveRepair: No delay, executing optimistic update immediately`);
      // Optimistic update for immediate UI feedback
      performOptimisticUpdate();
    }
    
    try {
      console.log(`📡 moveRepair: Calling API update for repairId=${repairId}`);
      // Use the API to update - it handles status_history and timestamps
      if (!updateRepair) {
        throw new Error('updateRepair is not available');
      }
      await updateRepair(repairId, { 
        status: newStatus,
        statusHistory: newHistory
      });
      console.log(`✅ moveRepair: API update completed for repairId=${repairId}`);
    } catch (error) {
      console.error('❌ moveRepair: Error updating repair status:', error);
      // Revert optimistic update on error
      setRepairs(initialRepairs);
      alert('Failed to update repair status. Please try again.');
    } finally {
      setUpdating(prev => ({ ...prev, [repairId]: false }));
    }
  };

  // Archive a repair (removes from active view)
  const finalizeRepair = async (repairId) => {
    setUpdating(prev => ({ ...prev, [repairId]: true }));
    
    const currentRepair = repairs.find(r => r.id === repairId);
    if (!currentRepair) {
      setUpdating(prev => ({ ...prev, [repairId]: false }));
      alert('Repair not found.');
      return;
    }
    
    // Optimistic update - remove from list
    setRepairs(prev => prev.filter(r => r.id !== repairId));
    
    // Close the summary panel
    setSummaryTicket(null);
    
    try {
      if (!archiveRepair) {
        throw new Error('archiveRepair is not available');
      }
      // Call the archive API
      await archiveRepair(repairId);
    } catch (error) {
      console.error('Error archiving repair:', error);
      // Revert optimistic update on error - add back to list
      setRepairs(prev => [...prev, currentRepair]);
      const errorMessage = error.data?.message || error.message || 'Unknown error';
      alert(`Failed to archive repair: ${errorMessage}`);
    } finally {
      setUpdating(prev => ({ ...prev, [repairId]: false }));
    }
  };

  // Update priority for a repair
  const updatePriority = async (repairId, newPriority) => {
    setUpdating(prev => ({ ...prev, [repairId]: true }));
    
    const currentRepair = repairs.find(r => r.id === repairId);
    if (!currentRepair) {
      setUpdating(prev => ({ ...prev, [repairId]: false }));
      alert('Repair not found.');
      return;
    }
    
    const oldPriority = currentRepair.priority;
    
    // Optimistic update
    setRepairs(prev => prev.map(r => 
      r.id === repairId ? { ...r, priority: newPriority } : r
    ));
    
    try {
      if (!updateRepair) {
        throw new Error('updateRepair is not available');
      }
      // Call the update API
      await updateRepair(repairId, { priority: newPriority });
    } catch (error) {
      console.error('Error updating priority:', error);
      // Revert optimistic update on error
      setRepairs(prev => prev.map(r => 
        r.id === repairId ? { ...r, priority: oldPriority } : r
      ));
      const errorMessage = error.data?.message || error.message || 'Unknown error';
      alert(`Failed to update priority: ${errorMessage}`);
    } finally {
      setUpdating(prev => ({ ...prev, [repairId]: false }));
    }
  };

  if (loading && repairs.length === 0) {
    return <div className={`h-full flex items-center justify-center ${isDark ? 'bg-slate-900 text-slate-400' : 'bg-slate-50 text-slate-500'}`}>Loading repairs...</div>;
  }

  // Vendor Inspection Form Component - shown as modal when vendor clicks "Fill Out Inspection"
  const VendorInspectionForm = ({ ticket, onCancel, onComplete, isNewInspection = false }) => {
    const { isDark } = useTheme(); // Get theme from context
    // Track last processed ticket ID to prevent infinite loops
    const lastTicketIdRef = useRef(null);
    
    // Local state for asset details (don't use parent's setSelectedAssetDetails)
    const [localAssetDetails, setLocalAssetDetails] = useState(null);
    
    // Template state
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [loadingTemplates, setLoadingTemplates] = useState(true);
    
    // Local state for repair checklist (don't use parent's repairChecklist)
    const [localRepairChecklist, setLocalRepairChecklist] = useState(() => {
      const initial = {};
      REPAIR_ITEMS.forEach(item => { initial[item.id] = 'Good'; });
      return initial;
    });
    
    // Local state for other damage (don't use parent's otherDamage)
    const [localOtherDamage, setLocalOtherDamage] = useState({ description: '', cost: '' });
    
    // Local state for vendor inspection images
    const [vendorInspectionImages, setVendorInspectionImages] = useState([]);
    
    // Dynamic checklist items (from template or default)
    const [checklistItems, setChecklistItems] = useState(REPAIR_ITEMS);

    // Auto-generate title and description for new inspections
    const generateDefaultTitle = () => {
      const today = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      // Generate a unique number based on timestamp (last 3 digits)
      const inspectionNum = Date.now().toString().slice(-3);
      return `(${today}) Repair Inspection #${inspectionNum}`;
    };
    
    const defaultDescription = 'Repair Inspection has been completed and these are the results.';
    
    // Initialize form with ticket data - hooks must be called before any early returns
    const [formData, setFormData] = useState({
      title: ticket?.title || (isNewInspection ? generateDefaultTitle() : ''),
      description: ticket?.description || (isNewInspection ? defaultDescription : ''),
      priority: ticket?.priority || 'Medium',
      notes: ticket?.notes || '',
      totalRepairCost: ticket?.total_repair_cost || '',
      damageDetails: ticket?.damage_details || {
        type: '',
        location: '',
        description: '',
        checklist: []
      }
    });

    // Load templates on mount (for vendors only)
    useEffect(() => {
      const loadTemplates = async () => {
        if (!isVendor) {
          setLoadingTemplates(false);
          return;
        }
        
        try {
          setLoadingTemplates(true);
          const assetType = ticket?.asset_type || localAssetDetails?.assetType;
          
          // Load templates applicable to this asset type
          const response = await api.get('/api/inspection-templates');
          const allTemplates = response;
          
          // Filter templates that apply to this asset type
          const applicableTemplates = allTemplates.filter(t => {
            if (!t.is_active) return false;
            if (!t.asset_types || t.asset_types.length === 0) return true; // No filter = applies to all
            return assetType && t.asset_types.includes(assetType);
          });
          
          setTemplates(applicableTemplates);
          
          // Auto-select default template if available
          const defaultTemplate = applicableTemplates.find(t => t.is_default);
          if (defaultTemplate) {
            handleTemplateSelect(defaultTemplate);
          }
        } catch (error) {
          console.error('Failed to load templates:', error);
        } finally {
          setLoadingTemplates(false);
        }
      };
      
      loadTemplates();
    }, [ticket?.asset_type, localAssetDetails?.assetType, isVendor]);

    // Handle template selection
    const handleTemplateSelect = (template) => {
      if (!template) {
        // Reset to default items
        setSelectedTemplate(null);
        setChecklistItems(REPAIR_ITEMS);
        const initial = {};
        REPAIR_ITEMS.forEach(item => { initial[item.id] = 'Good'; });
        setLocalRepairChecklist(initial);
        return;
      }

      setSelectedTemplate(template);
      
      // Parse checklist items from template
      const templateItems = typeof template.checklist_items === 'string'
        ? JSON.parse(template.checklist_items)
        : template.checklist_items;
      
      // Convert template items to checklist format
      const convertedItems = templateItems.map(item => ({
        id: item.id,
        label: item.label,
        cost: item.default_cost || 0,
        input_type: item.input_type,
        options: item.options
      }));
      
      setChecklistItems(convertedItems);
      
      // Initialize checklist state
      const initial = {};
      convertedItems.forEach(item => {
        if (item.input_type === 'select') {
          initial[item.id] = item.options?.[0] || 'Good';
        } else if (item.input_type === 'checkbox') {
          initial[item.id] = false;
        } else {
          initial[item.id] = '';
        }
      });
      setLocalRepairChecklist(initial);
    };

    // Load ticket data into the form when ticket ID changes
    useEffect(() => {
      if (!ticket || !ticket.id) return;
      
      // Only process if this is a different ticket
      if (lastTicketIdRef.current === ticket.id) return;
      lastTicketIdRef.current = ticket.id;
      
      const assetInfo = assetLookup[ticket.asset_id] || {};
      setLocalAssetDetails(assetInfo);
      
      setFormData({
        title: ticket.title || (isNewInspection ? generateDefaultTitle() : ''),
        description: ticket.description || (isNewInspection ? defaultDescription : ''),
        priority: ticket.priority || 'Medium',
        notes: ticket.notes || '',
        totalRepairCost: ticket.total_repair_cost || '',
        damageDetails: ticket.damage_details || {
          type: '',
          location: '',
          description: '',
          checklist: []
        }
      });

      // Initialize repair checklist from damage_details if available
      // damage_details may arrive as a JSON string from the DB — parse it safely
      try {
        const rawDamage = ticket.damage_details;
        const parsedDamage = typeof rawDamage === 'string' ? JSON.parse(rawDamage) : rawDamage;
        const rawChecklist = parsedDamage?.checklist;
        // checklist itself may also be a JSON string
        const checklist = typeof rawChecklist === 'string' ? JSON.parse(rawChecklist) : rawChecklist;

        if (Array.isArray(checklist) && checklist.length > 0) {
          const checklistState = {};
          checklist.forEach(item => {
            if (item?.id !== undefined) checklistState[item.id] = item.status;
          });
          setLocalRepairChecklist(checklistState);
        } else {
          const empty = {};
          REPAIR_ITEMS.forEach(item => { empty[item.id] = 'Good'; });
          setLocalRepairChecklist(empty);
        }
      } catch {
        // Malformed damage_details — fall back to defaults
        const empty = {};
        REPAIR_ITEMS.forEach(item => { empty[item.id] = 'Good'; });
        setLocalRepairChecklist(empty);
      }
      
      // Reset other damage
      setLocalOtherDamage({ description: '', cost: '' });
    }, [ticket?.id]); // Only depend on ticket ID

    // Early return after hooks are called
    if (!ticket) return null;

    const handleFormChange = (field, value) => {
      setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleDamageDetailsChange = (field, value) => {
      setFormData(prev => ({
        ...prev,
        damageDetails: { ...prev.damageDetails, [field]: value }
      }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setIsSubmitting(true);

      try {
        // Extract vendor inspection image IDs (files are already uploaded by ImageAttachment component)
        let vendorInspectionImageIds = [];
        if (vendorInspectionImages.length > 0) {
          // ImageAttachment component uploads files immediately when selected
          // So vendorInspectionImages contains already-uploaded file objects with IDs
          vendorInspectionImageIds = vendorInspectionImages
            .map(img => img.id)
            .filter(id => id != null); // Filter out any null/undefined IDs
          
          console.log(`Using ${vendorInspectionImageIds.length} vendor inspection images:`, vendorInspectionImageIds);
        }

        // Build checklist from local repair checklist state (using dynamic items)
        const checklist = checklistItems.map(item => {
          const value = localRepairChecklist[item.id];
          let status, cost;
          
          if (item.input_type === 'select' || !item.input_type) {
            status = value || 'Good';
            cost = value === 'Damaged' ? item.cost : 0;
          } else if (item.input_type === 'checkbox') {
            status = value ? 'Checked' : 'Unchecked';
            cost = value ? item.cost : 0;
          } else if (item.input_type === 'number') {
            status = value || '0';
            cost = parseFloat(value) || 0;
          } else {
            status = value || '';
            cost = 0;
          }
          
          return {
            id: item.id,
            label: item.label,
            status,
            cost,
            input_type: item.input_type
          };
        });

        // Add "other damage" if specified
        if (localOtherDamage.description && localOtherDamage.cost) {
          checklist.push({
            id: 'other',
            label: 'Other Damage',
            status: 'Damaged',
            cost: parseFloat(localOtherDamage.cost) || 0,
            description: localOtherDamage.description
          });
        }

        // Auto-generate damage location from checklist items marked as Damaged
        const damagedItems = checklist
          .filter(item => item.status === 'Damaged' || (item.status === 'Checked' && item.cost > 0))
          .map(item => item.label);
        
        const autoGeneratedLocation = damagedItems.length > 0 
          ? damagedItems.join(', ') 
          : formData.damageDetails.location || '';

        // Calculate total repair cost (including base inspection cost if template is used)
        const baseCost = selectedTemplate ? parseFloat(selectedTemplate.base_inspection_cost || 0) : 0;
        const itemsCost = checklist.reduce((sum, item) => sum + (item.cost || 0), 0);
        const totalCost = baseCost + itemsCost;

        const updatedDamageDetails = {
          ...formData.damageDetails,
          location: autoGeneratedLocation, // Auto-link damage locations
          checklist,
          vendor_inspection_images: vendorInspectionImageIds.length > 0 ? vendorInspectionImageIds : undefined,
          template_id: selectedTemplate?.id,
          base_inspection_cost: baseCost
        };

        // Check if this is a new inspection or updating existing
        if (isNewInspection || ticket.id === 'new') {
          // Create new repair ticket
          const newRepair = await createRepair({
            assetId: ticket.asset_id,
            title: formData.title,
            description: formData.description,
            priority: formData.priority,
            notes: formData.notes,
            totalRepairCost: totalCost,
            damageDetails: updatedDamageDetails,
            status: 'Needs Approval',
            companyId: ticket.company_id,
            inspectionTemplateId: selectedTemplate?.id
          });

          console.log('✅ New inspection created:', newRepair);
          
          // Add to repairs list
          if (newRepair) {
            setRepairs(prev => [newRepair, ...prev]);
          }
        } else {
          // Update existing repair with inspection details and move to "Needs Approval"
          await updateRepair(ticket.id, {
            title: formData.title,
            description: formData.description,
            priority: formData.priority,
            notes: formData.notes,
            totalRepairCost: totalCost,
            damageDetails: updatedDamageDetails,
            status: 'Needs Approval',
            inspectionTemplateId: selectedTemplate?.id,
            statusHistory: [
              ...(ticket.status_history || []),
              { status: 'Needs Approval', timestamp: new Date().toISOString() }
            ]
          });

          // Delay local state update by 2000ms to allow animation to complete
          console.log('⏳ Vendor Inspection Form: Scheduling state update in 2000ms');
          setTimeout(() => {
            console.log('⏰ Vendor Inspection Form: Delay completed, updating repairs state');
            setRepairs(prev => prev.map(r => 
              r.id === ticket.id 
                ? { 
                    ...r, 
                    status: 'Needs Approval',
                    title: formData.title,
                    description: formData.description,
                    priority: formData.priority,
                    notes: formData.notes,
                    total_repair_cost: totalCost,
                    damage_details: updatedDamageDetails
                  } 
                : r
            ));
          }, 2000);
        }

        // Trigger completion callback
        console.log('🎬 Vendor Inspection Form: Calling onComplete');
        if (onComplete) {
          onComplete();
        }
        
      } catch (error) {
        console.error('Error submitting inspection:', error);
        alert('Failed to submit inspection. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    };

    // Get asset image URL
    const assetImageUrl = ticket.asset_image_url || 
      (ticket.asset_type ? getAssetImageUrl(ticket.asset_type + '.png') : null);

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className={`border rounded-xl w-full max-w-5xl max-h-[90vh] shadow-2xl flex flex-col ${isDark ? 'bg-gray-800/95 border-gray-700' : 'bg-white border-gray-200'}`}>
          {/* Header */}
          <div className={`flex items-center justify-between p-4 sm:p-6 border-b shrink-0 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center gap-4">
            {assetImageUrl && (
              <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-slate-800 rounded-xl overflow-hidden flex-shrink-0 border-2 ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
                <img 
                  src={assetImageUrl} 
                  alt={ticket.asset_name || 'Asset'} 
                  className="w-full h-full object-contain p-2"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            <div>
              <h2 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {isNewInspection ? 'Create New Inspection' : 'Fill Out Repair Inspection'}
              </h2>
              <p className={`text-xs sm:text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Complete the repair details and send to owner for approval
              </p>
            </div>
          </div>
          <button 
            onClick={() => {
              if (onCancel) onCancel();
            }}
            className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
          >
            <X size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
          </button>
        </div>

        {/* Form Content - Scrollable */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 hide-scrollbar">
          <div className="grid md:grid-cols-2 gap-5">
            {/* Left column */}
            <div className="space-y-4">
              {/* Asset Info - Read-only */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Asset</label>
                <div className="w-full px-3 py-2 bg-slate-900/60 border border-slate-700 rounded-lg text-slate-400 text-sm">
                  {localAssetDetails ? 
                    `${localAssetDetails.assetNumber || ticket.asset_id} - ${localAssetDetails.assetName || 'Unknown'}` 
                    : ticket.asset_id}
                </div>
              </div>

              {/* Auto-filled asset info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-3">
                  <div className="text-xs text-slate-500 uppercase mb-1">Owner Name</div>
                  <div className="text-sm text-slate-200 font-medium">{localAssetDetails?.customerName || '—'}</div>
                </div>
                <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-3">
                  <div className="text-xs text-slate-500 uppercase mb-1">Brand</div>
                  <div className="text-sm text-slate-200 font-medium">{localAssetDetails?.brand || '—'}</div>
                </div>
                <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-3">
                  <div className="text-xs text-slate-500 uppercase mb-1">Asset Name</div>
                  <div className="text-sm text-slate-200 font-medium">{localAssetDetails?.assetName || '—'}</div>
                </div>
                <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-3">
                  <div className="text-xs text-slate-500 uppercase mb-1">Asset Number</div>
                  <div className="text-sm text-slate-200 font-medium">{localAssetDetails?.assetNumber || '—'}</div>
                </div>
              </div>

              {/* Title & Description */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Title <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                  placeholder="Brief description of the issue"
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2.5 text-slate-200 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  placeholder="Detailed description of the repair needed..."
                  rows={3}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2.5 text-slate-200 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
                />
              </div>

              {/* Damage Details Section */}
              <div className="border border-slate-700 rounded-lg p-4 space-y-3">
                <h3 className="text-sm font-medium text-slate-300">Damage Details</h3>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Damage Type</label>
                    <select
                      value={formData.damageDetails.type}
                      onChange={(e) => handleDamageDetailsChange('type', e.target.value)}
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500 cursor-pointer"
                    >
                      <option value="">Select type</option>
                      {DAMAGE_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Damage Location</label>
                    <input
                      type="text"
                      value={formData.damageDetails.location}
                      onChange={(e) => handleDamageDetailsChange('location', e.target.value)}
                      placeholder="e.g., Left side, Top panel"
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Damage Description</label>
                  <textarea
                    value={formData.damageDetails.description}
                    onChange={(e) => handleDamageDetailsChange('description', e.target.value)}
                    placeholder="Describe the damage in detail..."
                    rows={2}
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500 resize-none"
                  />
                </div>

                {/* Image Attachments */}
                <div>
                  <label className="block text-xs text-slate-400 mb-1">
                    <span className="flex items-center gap-1">
                      <Image size={14} />
                      Inspection Images
                    </span>
                  </label>
                  <ImageAttachment
                    onAttachmentsChange={setVendorInspectionImages}
                    attachments={vendorInspectionImages}
                    maxFiles={10}
                    uploadEndpoint="/api/files/upload"
                    metadata={{ repairId: ticket?.id, assetId: ticket?.asset_id, category: 'vendor_inspection' }}
                  />
                </div>
              </div>
            </div>

            {/* Right column */}
            <div className="space-y-4">
              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => handleFormChange('priority', e.target.value)}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2.5 text-slate-200 focus:outline-none focus:border-blue-500 cursor-pointer"
                >
                  {PRIORITY_OPTIONS.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              {/* Template Selector (for vendors) */}
              {isVendor && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Inspection Template
                  </label>
                  {loadingTemplates ? (
                    <div className="text-xs text-slate-500">Loading templates...</div>
                  ) : (
                    <select
                      value={selectedTemplate?.id || ''}
                      onChange={(e) => {
                        const template = templates.find(t => t.id === e.target.value);
                        handleTemplateSelect(template);
                      }}
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2.5 text-slate-200 focus:outline-none focus:border-blue-500 cursor-pointer"
                    >
                      <option value="">Default Template</option>
                      {templates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.template_name}
                          {template.is_default && ' (Default)'}
                        </option>
                      ))}
                    </select>
                  )}
                  {selectedTemplate && (
                    <p className="text-xs text-slate-500 mt-1">
                      {selectedTemplate.description || `${checklistItems.length} items`}
                    </p>
                  )}
                </div>
              )}

              {/* Repair Checklist */}
              <div className="border border-slate-700 rounded-lg p-4 space-y-3">
                <h3 className="text-sm font-medium text-slate-300">Repair Checklist</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto hide-scrollbar">
                  {checklistItems.map(item => (
                    <div key={item.id} className="flex items-center justify-between bg-slate-900/40 border border-slate-800 rounded-lg p-2">
                      <div className="flex-1">
                        <div className="text-xs text-slate-300">{item.label}</div>
                        <div className="text-xs text-slate-500">${item.cost}</div>
                      </div>
                      
                      {/* Dynamic input based on item type */}
                      {item.input_type === 'select' || !item.input_type ? (
                        <select
                          value={localRepairChecklist[item.id] || (item.options?.[0] || 'Good')}
                          onChange={(e) => setLocalRepairChecklist(prev => ({ ...prev, [item.id]: e.target.value }))}
                          className={`text-xs px-2 py-1 rounded border ${
                            localRepairChecklist[item.id] === 'Damaged' 
                              ? 'bg-red-900/30 text-red-400 border-red-500/50' 
                              : 'bg-emerald-900/30 text-emerald-400 border-emerald-500/50'
                          } cursor-pointer`}
                        >
                          {item.options ? item.options.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          )) : (
                            <>
                              <option value="Good">Good</option>
                              <option value="Damaged">Damaged</option>
                            </>
                          )}
                        </select>
                      ) : item.input_type === 'checkbox' ? (
                        <input
                          type="checkbox"
                          checked={localRepairChecklist[item.id] || false}
                          onChange={(e) => setLocalRepairChecklist(prev => ({ ...prev, [item.id]: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                      ) : item.input_type === 'number' ? (
                        <input
                          type="number"
                          step="0.01"
                          value={localRepairChecklist[item.id] || ''}
                          onChange={(e) => setLocalRepairChecklist(prev => ({ ...prev, [item.id]: e.target.value }))}
                          className="text-xs px-2 py-1 w-20 rounded border bg-slate-950 text-slate-200 border-slate-700 focus:outline-none focus:border-blue-500"
                        />
                      ) : (
                        <input
                          type="text"
                          value={localRepairChecklist[item.id] || ''}
                          onChange={(e) => setLocalRepairChecklist(prev => ({ ...prev, [item.id]: e.target.value }))}
                          className="text-xs px-2 py-1 w-32 rounded border bg-slate-950 text-slate-200 border-slate-700 focus:outline-none focus:border-blue-500"
                          placeholder="Enter value"
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* Other Damage */}
                <div className="mt-3 pt-3 border-t border-slate-700">
                  <div className="text-xs text-slate-400 mb-2">Other Damage</div>
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={localOtherDamage.description}
                      onChange={(e) => setLocalOtherDamage(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Description"
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-blue-500"
                    />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={localOtherDamage.cost}
                      onChange={(e) => setLocalOtherDamage(prev => ({ ...prev, cost: e.target.value }))}
                      placeholder="Cost ($)"
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Total Cost Display */}
                <div className="mt-3 pt-3 border-t border-slate-700">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-slate-300">Estimated Total Cost:</span>
                    <span className="text-lg font-bold text-emerald-400">
                      ${(
                        (selectedTemplate ? parseFloat(selectedTemplate.base_inspection_cost || 0) : 0) +
                        checklistItems.reduce((sum, item) => {
                          const value = localRepairChecklist[item.id];
                          // For select type, check if value is "Damaged"
                          if (item.input_type === 'select' || !item.input_type) {
                            return sum + (value === 'Damaged' ? item.cost : 0);
                          }
                          // For checkbox, add cost if checked
                          if (item.input_type === 'checkbox') {
                            return sum + (value ? item.cost : 0);
                          }
                          // For number/text, use the value as cost if it's a number
                          if (item.input_type === 'number') {
                            return sum + (parseFloat(value) || 0);
                          }
                          return sum;
                        }, 0) + (parseFloat(localOtherDamage.cost) || 0)
                      ).toFixed(2)}
                    </span>
                  </div>
                  {selectedTemplate && selectedTemplate.base_inspection_cost > 0 && (
                    <p className="text-xs text-slate-500 mt-1">
                      Includes base inspection cost: ${parseFloat(selectedTemplate.base_inspection_cost).toFixed(2)}
                    </p>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Vendor Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  placeholder="Additional notes or recommendations..."
                  rows={4}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2.5 text-slate-200 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-slate-700">
            <button
              type="button"
              onClick={() => {
                if (onCancel) onCancel();
              }}
              className="flex-1 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Sending to Owner...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Send to Owner for Approval
                </>
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
    );
  };

  // Owner/User role: Now uses Kanban view (horizontal columns) same as assigned vendors
  // Customers have restricted actions compared to vendors and admins
  // Previously rendered CustomerRepairsView (timeline style), now using Kanban for consistency
  
  // Assigned Vendor, Admin/Manager, and Owner roles: Render Kanban view
  // Vendors and Customers have restricted actions, Admins have full access
  // isVendor, isAdmin, and isCustomer are already destructured from usePermissions()

  console.log('📋 [Workflows Page] ✅ About to render JSX:', {
    repairsCount: repairs.length,
    filteredCount: filteredRepairs.length,
    hasUser: !!user
  });

  // Extract kanban board into a variable to avoid JSX adjacent-element parse errors
  const kanbanBoard = (
    <div className="flex-1 overflow-x-auto px-4 sm:px-6 pb-6 hide-scrollbar">
      <div className="flex gap-4 h-full min-w-0">
        {columns.map((col) => {
          const columnRepairs = filteredRepairs.filter(t => t.status === col.id);
          const isColumnDimmed = kanbanStatusFilter !== 'all' && kanbanStatusFilter !== col.id;
          const getColumnBgColor = () => {
            switch (col.id) {
              case 'Needs Repair': case 'Sent to Vendor': return isDark ? 'bg-red-500/15' : 'bg-red-100';
              case 'Needs Approval': return isDark ? 'bg-yellow-500/15' : 'bg-yellow-100';
              case 'In Repair': return isDark ? 'bg-blue-500/15' : 'bg-blue-100';
              case 'Complete': return isDark ? 'bg-emerald-500/15' : 'bg-emerald-100';
              default: return isDark ? 'bg-gray-800/30' : 'bg-white';
            }
          };
          const getColumnBorderColor = () => {
            switch (col.id) {
              case 'Needs Repair': case 'Sent to Vendor': return isDark ? 'border-red-500/50 hover:border-red-500' : 'border-red-500/50 hover:border-red-500';
              case 'Needs Approval': return isDark ? 'border-yellow-500/50 hover:border-yellow-500' : 'border-yellow-500/50 hover:border-yellow-500';
              case 'In Repair': return isDark ? 'border-blue-500/50 hover:border-blue-500' : 'border-blue-500/50 hover:border-blue-500';
              case 'Complete': return isDark ? 'border-emerald-500/50 hover:border-emerald-500' : 'border-emerald-500/50 hover:border-emerald-500';
              default: return isDark ? 'border-gray-700 hover:border-gray-600' : 'border-gray-200 hover:border-gray-300';
            }
          };
          const getSelectedColors = (status) => {
            switch (status) {
              case 'Needs Repair': case 'Sent to Vendor': return { bg: isDark ? 'bg-red-500/25' : 'bg-red-200' };
              case 'Needs Approval': return { bg: isDark ? 'bg-yellow-500/25' : 'bg-yellow-200' };
              case 'In Repair': return { bg: isDark ? 'bg-blue-500/25' : 'bg-blue-200' };
              case 'Complete': return { bg: isDark ? 'bg-emerald-500/25' : 'bg-emerald-200' };
              default: return { bg: 'bg-purple-500/10' };
            }
          };
          const getRingColor = (status, selected) => {
            if (!selected) return 'transparent';
            switch (status) {
              case 'Needs Repair': case 'Sent to Vendor': return 'rgba(239, 68, 68, 0.5)';
              case 'Needs Approval': return 'rgba(234, 179, 8, 0.5)';
              case 'In Repair': return 'rgba(59, 130, 246, 0.5)';
              case 'Complete': return 'rgba(16, 185, 129, 0.5)';
              default: return 'rgba(168, 85, 247, 0.5)';
            }
          };
          return (
            <div key={col.id} className={`w-96 flex-shrink-0 flex flex-col rounded-xl border transition-opacity duration-300 ease-out ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'} ${isColumnDimmed ? 'opacity-40' : 'opacity-100'}`}>
              <div className={`p-4 border-t-2 ${col.color} rounded-t-xl flex justify-between items-center ${isDark ? 'bg-slate-900' : 'bg-gray-50'}`}>
                <h3 className={`font-semibold text-base ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{col.title}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-slate-800 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>{columnRepairs.length}</span>
              </div>
              <div className="flex-1 p-3 overflow-y-auto space-y-3 hide-scrollbar">
                {columnRepairs.map((ticket) => {
                  const assetImageUrl = ticket.asset_image_url || (ticket.asset_type ? getAssetImageUrl(ticket.asset_type + '.png') : null);
                  const isNeedsRepairSelected = ticket.status === 'Needs Repair' && selectedNeedsRepairCard?.id === ticket.id;
                  const isSelected = selectedRepairForDetails?.id === ticket.id || isNeedsRepairSelected;
                  const hasSelection = selectedRepairForDetails !== null || selectedNeedsRepairCard !== null;
                  const selColors = getSelectedColors(col.id);
                  const ringColor = getRingColor(col.id, isSelected);
                  return (
                    <div
                      key={ticket.id}
                      className={`p-3 rounded-lg shadow-sm hover:shadow-md transition-[opacity,box-shadow] duration-300 ease-out group relative cursor-pointer border ${isSelected ? 'border-transparent' : `${getColumnBgColor()} ${getColumnBorderColor()}`} ${isSelected ? selColors.bg : ''} ${isSelected ? 'z-10' : ''} ${hasSelection && !isSelected ? 'opacity-40' : 'opacity-100'}`}
                      style={{ willChange: isSelected || hasSelection ? 'opacity, box-shadow' : 'auto', boxShadow: isSelected ? `0 0 0 2px ${ringColor}, 0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)` : undefined, borderWidth: '1px' }}
                      onClick={() => handleTicketClick(ticket)}
                    >
                      {/* Inspection Button - shown when a Needs Repair card is selected */}
                      {isNeedsRepairSelected && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openInspectionForm(ticket);
                          }}
                          className="absolute top-2 right-2 flex items-center gap-1.5 px-2.5 py-1.5 bg-red-600 hover:bg-red-500 text-white text-xs font-semibold rounded-lg transition-colors z-10 shadow-md"
                        >
                          <Wrench size={12} />
                          Inspection
                        </button>
                      )}
                      {/* Back to In Repair Button - Only for Complete status in workflow module (vendor view) */}
                      {ticket.status === 'Complete' && (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm('Move this repair back to "In Repair" status? This will undo the completion.')) {
                              try {
                                await moveRepair(ticket.id, 'In Repair');
                                if (summaryTicket?.id === ticket.id) {
                                  setSummaryTicket(prev => prev ? { ...prev, status: 'In Repair' } : null);
                                }
                              } catch (error) {
                                console.error('Error moving repair back to In Repair:', error);
                                alert('Failed to move repair back to In Repair status. Please try again.');
                              }
                            }
                          }}
                          className={`absolute top-2 right-2 p-1.5 opacity-0 group-hover:opacity-100 hover:bg-blue-600 rounded-lg transition-all z-10 ${isDark ? 'text-blue-300 hover:text-white bg-blue-900/80' : 'text-blue-600 hover:text-white bg-blue-50/80'}`}
                        >
                          <ArrowLeft size={14} />
                        </button>
                      )}
                      <div className="flex items-center gap-3 mb-3">
                        {assetImageUrl && (
                          <div className="w-20 h-20 bg-slate-800 rounded-lg overflow-hidden flex-shrink-0 border border-slate-600">
                            <img src={assetImageUrl} alt={ticket.asset_name || 'Asset'} className="w-full h-full object-contain p-1" onError={(e) => { e.target.style.display = 'none'; }} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className={`text-lg font-bold truncate ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{ticket.asset_name || ticket.asset_id}</div>
                          <div className={`text-sm font-medium truncate mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>ID: {ticket.asset_id}</div>
                        </div>
                      </div>
                      <div className={`flex items-center gap-3 text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        {ticket.total_repair_cost && (
                          <span className="flex items-center gap-1.5 text-emerald-500 font-bold text-base">
                            <DollarSign size={18} />{parseFloat(ticket.total_repair_cost).toFixed(2)}
                          </span>
                        )}
                        {ticket.comment_count > 0 && (
                          <span className="flex items-center gap-1"><MessageSquare size={12} />{ticket.comment_count}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {columnRepairs.length === 0 && (
                  <div className={`text-center py-8 text-sm border-2 border-dashed rounded-lg ${isDark ? 'text-slate-600 border-slate-800' : 'text-slate-400 border-slate-200'}`}>No repairs</div>
                )}
              </div>
            </div>
          );
        })}

        {/* Repair Details Panel */}
        <div className="flex-1 min-w-0 max-w-full h-full">
          {selectedRepairForDetails ? (
            <DetailsModal
              ticket={selectedRepairForDetails}
              isVendor={true}
              isCustomer={false}
              isDark={isDark}
              repairFiles={repairFiles}
              getRepairMessages={getRepairMessages}
              loadRepairFiles={loadRepairFiles}
              moveRepair={moveRepair}
              setSummaryTicket={setSummaryTicket}
              updatePriority={updatePriority}
              onClose={() => { setSelectedRepairForDetails(null); }}
            />
          ) : selectedNeedsRepairCard ? (
            <div className="h-full">
              <ChatThread
                repairId={selectedNeedsRepairCard.id}
                isOpen={true}
                onClose={null}
                title="Chat"
                compact={true}
              />
            </div>
          ) : (
            <div className={`flex flex-col rounded-xl border-2 border-dashed items-center justify-center h-full ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="text-center p-8">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
                  <FileText size={36} className={isDark ? 'text-slate-600' : 'text-slate-400'} />
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Repair Details</h3>
                <p className={`text-sm max-w-[300px] ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>Click on any repair card to view its details, images, and chat</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <FeatureGate 
      user={user} 
      featureKey="repairs"
      blur={true}
      showUpgradePrompt={true}
      upgradePlanName="Pro"
      customMessage="Repair management requires an active subscription."
    >
      <div className={`h-[calc(100vh-8rem)] flex flex-col overflow-hidden ${isDark ? 'bg-slate-900 text-slate-100' : 'bg-slate-50 text-slate-900'}`}>
        <style>{hideScrollbarStyle}</style>
      
      {/* Tab Navigation */}
      <div className={`flex gap-1 px-4 sm:px-6 pt-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <button
          onClick={() => navigate('/workflows')}
          className={`px-4 py-2.5 font-medium text-sm rounded-t-lg transition-colors ${
            location.pathname === '/workflows'
              ? isDark
                ? 'bg-slate-800 text-white border-b-2 border-blue-500'
                : 'bg-white text-slate-900 border-b-2 border-blue-500'
              : isDark
                ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
        >
          Repairs
        </button>
        <button
          onClick={() => navigate('/workflows/templates')}
          className={`px-4 py-2.5 font-medium text-sm rounded-t-lg transition-colors ${
            location.pathname === '/workflows/templates'
              ? isDark
                ? 'bg-slate-800 text-white border-b-2 border-blue-500'
                : 'bg-white text-slate-900 border-b-2 border-blue-500'
              : isDark
                ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
        >
          Templates
        </button>
      </div>
      
      {/* Header with Title and Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4 px-4 sm:px-6 pt-6 shrink-0">
        <div className="flex gap-2 w-full sm:w-auto items-center">
          {/* View Toggle: Kanban ↔ List */}
          <div className={`flex rounded-lg border overflow-hidden ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
            <button
              onClick={() => setRepairViewMode('kanban')}
              title="Kanban View"
              className={`px-3 py-2 flex items-center gap-1.5 text-sm transition-colors ${
                repairViewMode === 'kanban'
                  ? isDark ? 'bg-slate-600 text-white' : 'bg-slate-200 text-slate-900'
                  : isDark ? 'bg-slate-800 text-slate-400 hover:text-slate-200' : 'bg-white text-gray-500 hover:text-gray-800'
              }`}
            >
              <LayoutGrid size={15} />
              <span className="hidden sm:inline">Kanban</span>
            </button>
            <button
              onClick={() => setRepairViewMode('list')}
              title="List View"
              className={`px-3 py-2 flex items-center gap-1.5 text-sm transition-colors ${
                repairViewMode === 'list'
                  ? isDark ? 'bg-slate-600 text-white' : 'bg-slate-200 text-slate-900'
                  : isDark ? 'bg-slate-800 text-slate-400 hover:text-slate-200' : 'bg-white text-gray-500 hover:text-gray-800'
              }`}
            >
              <List size={15} />
              <span className="hidden sm:inline">List</span>
            </button>
          </div>

          {/* New Ticket - Admins only (workflow starts from failed routine inspections) */}
          {isAdmin && (
            <button 
              onClick={() => setShowNewTicketModal(true)}
              className="text-sm bg-blue-600 hover:bg-blue-500 text-white px-3 py-2 rounded-lg transition-colors flex items-center gap-1"
            >
              <Plus size={16} />
              New Ticket
            </button>
          )}
        </div>
      </div>

      {/* Status Filter Tiles + Filters */}
      <div className="px-4 sm:px-6 mb-4 shrink-0">
        {/* Compact status filter tiles — rendered for both views, each with its own filter state */}
        {(() => {
          const activeFilter = repairViewMode === 'kanban' ? kanbanStatusFilter : listStatusFilter;
          const setActiveFilter = repairViewMode === 'kanban' ? setKanbanStatusFilter : setListStatusFilter;
          const getTileAccentColor = (id) => {
            switch (id) {
              case 'Needs Repair': case 'Sent to Vendor': return isDark ? 'border-red-500/60 hover:border-red-400' : 'border-red-300 hover:border-red-500';
              case 'Needs Approval': return isDark ? 'border-yellow-500/60 hover:border-yellow-400' : 'border-yellow-300 hover:border-yellow-500';
              case 'In Repair': return isDark ? 'border-blue-500/60 hover:border-blue-400' : 'border-blue-300 hover:border-blue-500';
              case 'Complete': return isDark ? 'border-emerald-500/60 hover:border-emerald-400' : 'border-emerald-300 hover:border-emerald-500';
              default: return isDark ? 'border-slate-600 hover:border-slate-400' : 'border-gray-200 hover:border-gray-400';
            }
          };
          const getTileActiveBg = (id) => {
            switch (id) {
              case 'Needs Repair': case 'Sent to Vendor': return isDark ? 'bg-red-500/25 border-red-400' : 'bg-red-100 border-red-500';
              case 'Needs Approval': return isDark ? 'bg-yellow-500/25 border-yellow-400' : 'bg-yellow-100 border-yellow-500';
              case 'In Repair': return isDark ? 'bg-blue-500/25 border-blue-400' : 'bg-blue-100 border-blue-500';
              case 'Complete': return isDark ? 'bg-emerald-500/25 border-emerald-400' : 'bg-emerald-100 border-emerald-500';
              default: return isDark ? 'bg-slate-700 border-slate-400' : 'bg-gray-100 border-gray-400';
            }
          };
          return (
            <div className="flex items-start gap-4 mb-4">
              {/* Tiles group */}
              <div className="flex flex-wrap gap-2 flex-shrink-0">
              {/* "All" tile */}
              <button
                onClick={() => { setActiveFilter('all'); if (repairViewMode === 'kanban') { setSelectedNeedsRepairCard(null); setSelectedRepairForDetails(null); } }}
                className={`flex flex-col items-start px-4 py-3 rounded-xl border-2 transition-all min-w-[90px] ${
                  activeFilter === 'all'
                    ? isDark
                      ? 'bg-slate-700 border-slate-400 text-white'
                      : 'bg-slate-100 border-slate-500 text-slate-900'
                    : isDark
                      ? 'bg-slate-800/50 border-slate-700 text-slate-300 hover:border-slate-500'
                      : 'bg-white border-gray-200 text-gray-700 hover:border-gray-400'
                }`}
              >
                <span className="text-2xl font-bold leading-none">{filteredRepairs.length}</span>
                <span className="text-xs mt-1 font-medium">All</span>
              </button>

              {/* Per-status tiles */}
              {columns.map(col => {
                const colCount = filteredRepairs.filter(r => r.status === col.id).length;
                const isActive = activeFilter === col.id;
                const handleTileClick = () => {
                  const nextFilter = isActive ? 'all' : col.id;
                  setActiveFilter(nextFilter);
                  if (repairViewMode === 'kanban') {
                    if (nextFilter === 'all') {
                      // Reset all selections
                      setSelectedNeedsRepairCard(null);
                      setSelectedRepairForDetails(null);
                    } else if (col.id === 'Needs Repair' || col.id === 'Sent to Vendor') {
                      // Switching to Needs Repair: clear details selection, highlight first card
                      setSelectedRepairForDetails(null);
                      const firstRepair = filteredRepairs.find(r => r.status === col.id);
                      if (firstRepair) setSelectedNeedsRepairCard(firstRepair);
                    } else {
                      // Switching to any other column: clear Needs Repair highlight, auto-select first card
                      setSelectedNeedsRepairCard(null);
                      const firstRepair = filteredRepairs.find(r => r.status === col.id);
                      if (firstRepair) handleTicketClick(firstRepair);
                    }
                  }
                };
                return (
                  <button
                    key={col.id}
                    onClick={handleTileClick}
                    className={`flex flex-col items-start px-4 py-3 rounded-xl border-2 transition-all min-w-[110px] ${
                      isActive
                        ? getTileActiveBg(col.id)
                        : `${isDark ? 'bg-slate-800/50' : 'bg-white'} ${getTileAccentColor(col.id)}`
                    }`}
                  >
                    <span className={`text-2xl font-bold leading-none ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {colCount}
                    </span>
                    <span className={`text-xs mt-1 font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                      {col.title}
                    </span>
                    {/* Dollar total on relevant tiles (customer view only) */}
                    {effectiveIsCustomer && col.id === 'Needs Approval' && (
                      <span className={`text-xs mt-0.5 font-semibold ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
                        ${customerRepairTotals.totalNeedsApproval.toFixed(2)}
                      </span>
                    )}
                    {effectiveIsCustomer && (col.id === 'In Repair' || col.id === 'Complete') && (
                      <span className={`text-xs mt-0.5 font-semibold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        ${customerRepairTotals.totalInRepairAndComplete.toFixed(2)}
                      </span>
                    )}
                  </button>
                );
              })}
              </div>

              {/* Selected repair asset info — fills the empty space to the right of the tiles */}
              {selectedRepairForDetails && (() => {
                const t = selectedRepairForDetails;
                const imgUrl = t.asset_image_url || (t.asset_type ? getAssetImageUrl(t.asset_type + '.png') : null);
                const files = repairFiles && repairFiles[t.id] ? repairFiles[t.id] : [];
                const regularImages = files.filter(file => {
                  if (file._source === 'inspection') return false;
                  let imageUrl = file.viewUrl || file.view_url || file.url;
                  if (imageUrl && imageUrl.startsWith('gs://')) return false;
                  return file.mime_type?.startsWith('image/') || file.type?.startsWith('image/') || (imageUrl && !imageUrl.startsWith('gs://'));
                });
                const hasImages = regularImages.length > 0;
                const getStatusColor = (status) => {
                  switch (status) {
                    case 'Needs Repair': case 'Sent to Vendor': return isDark ? 'text-red-400 bg-red-500/15 border-red-500/40' : 'text-red-700 bg-red-50 border-red-200';
                    case 'Needs Approval': return isDark ? 'text-yellow-400 bg-yellow-500/15 border-yellow-500/40' : 'text-yellow-700 bg-yellow-50 border-yellow-200';
                    case 'In Repair': return isDark ? 'text-blue-400 bg-blue-500/15 border-blue-500/40' : 'text-blue-700 bg-blue-50 border-blue-200';
                    case 'Complete': return isDark ? 'text-emerald-400 bg-emerald-500/15 border-emerald-500/40' : 'text-emerald-700 bg-emerald-50 border-emerald-200';
                    default: return isDark ? 'text-slate-400 bg-slate-700 border-slate-600' : 'text-gray-600 bg-gray-100 border-gray-200';
                  }
                };
                return (
                  <div className={`flex items-center gap-3 flex-1 min-w-0 px-4 py-2 rounded-xl border ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'}`}>
                    {imgUrl && (
                      <div className={`w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border ${isDark ? 'bg-slate-900 border-slate-600' : 'bg-gray-100 border-gray-300'}`}>
                        <img src={imgUrl} alt={t.asset_name || 'Asset'} className="w-full h-full object-contain p-1" onError={(e) => { e.target.style.display = 'none'; }} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-bold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{t.asset_name || t.asset_id}</p>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>ID: <span className={`font-medium ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>{t.asset_id || '—'}</span></span>
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${getStatusColor(t.status)}`}>{t.status || '—'}</span>
                        {hasImages && (
                          <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                            <Image size={12} />
                            {regularImages.length} image{regularImages.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })()}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search Filter */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by asset name, ID, or type..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className={`w-full px-4 py-2 rounded-lg border text-sm ${
                isDark 
                  ? 'bg-slate-800 border-slate-700 text-slate-200 placeholder-slate-500' 
                  : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          {/* Assigned Vendor Filter (for owners) - Searchable */}
          {(effectiveIsCustomer || (isAdmin && viewMode === 'customer')) && uniqueAssignedVendors.length > 0 && (
            <div className="w-64">
              <SearchableSelect
                options={[{ id: 'all', name: 'All Assigned Vendors' }, ...uniqueVendors.map(v => ({ id: v, name: v }))]}
                value={vendorFilter}
                onChange={setVendorFilter}
                placeholder="Filter by assigned vendor..."
                searchPlaceholder="Search vendors..."
                getOptionLabel={(opt) => opt.name}
                getOptionValue={(opt) => opt.id}
                isDark={isDark}
              />
            </div>
          )}

          {/* Owner Filter (for assigned vendors) - Searchable */}
          {effectiveIsAssignedVendor && uniqueCompanies.length > 0 && (
            <div className="w-64">
              <SearchableSelect
                options={[{ id: 'all', name: 'All Customers' }, ...uniqueCompanies.map(c => ({ id: c, name: c }))]}
                value={customerFilter}
                onChange={setCustomerFilter}
                placeholder="Filter by owner..."
                searchPlaceholder="Search customers..."
                getOptionLabel={(opt) => opt.name}
                getOptionValue={(opt) => opt.id}
                isDark={isDark}
              />
            </div>
          )}
        </div>
      </div>

      {/* Board / List Area — list view */}
      {repairViewMode === 'list' && (
        <div className="flex-1 overflow-hidden px-4 sm:px-6 pb-6 flex flex-col min-h-0">
          <RepairListView
            filteredRepairs={filteredRepairs}
            columns={columns}
            isDark={isDark}
            onTicketClick={handleTicketClick}
            effectiveIsCustomer={effectiveIsCustomer}
            effectiveIsAssignedVendor={effectiveIsAssignedVendor}
            customerRepairTotals={customerRepairTotals}
            repairFiles={repairFiles}
            loadRepairFiles={loadRepairFiles}
            moveRepair={moveRepair}
            user={user}
            onFillInspection={(ticket) => {
              openInspectionForm(ticket);
            }}
            statusFilterControlled={listStatusFilter}
            onStatusFilterChange={setListStatusFilter}
          />
        </div>
      )}

      {/* Board / List Area — kanban view */}
      {repairViewMode === 'kanban' && kanbanBoard}

      {/* Column Details Modal */}
      {columnDetailsModal && (
        <ColumnDetailsModal
          columnId={columnDetailsModal.columnId}
          columnTitle={columnDetailsModal.columnTitle}
          repairs={columnDetailsModal.repairs}
          isDark={isDark}
          onClose={() => setColumnDetailsModal(null)}
          onSelectRepair={async (repair) => {
            // Normalize the repair object
            const normalizedRepair = {
              ...repair,
              damage_details: repair.damage_details 
                ? (typeof repair.damage_details === 'string' 
                    ? JSON.parse(repair.damage_details) 
                    : repair.damage_details)
                : {}
            };
            setSelectedRepairForDetails(normalizedRepair);
            // Load files for the selected repair
            await loadRepairFiles(repair.id);
          }}
        />
      )}

      {/* Lightbox for images - still a modal overlay */}

      {/* New Ticket Modal */}
      {showNewTicketModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-800/30 border border-gray-700 rounded-xl w-full max-w-5xl max-h-[85vh] mx-4 shadow-2xl flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-800">
              <div>
                <h2 className="text-xl font-bold text-white">
                  {editingRepairId ? 'Fill Out Repair Inspection' : 'Create New Ticket'}
                </h2>
                <p className="text-sm text-slate-400 mt-1">
                      {editingRepairId ? 'Complete the repair details and send to owner for approval' : 'Submit a repair request for an asset'}
                </p>
              </div>
              <button 
                onClick={() => {
                  setShowNewTicketModal(false);
                  setEditingRepairId(null);
                  resetRepairForm();
                }}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X size={20} className="text-slate-400" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleCreateTicket} className="p-6 space-y-4 overflow-y-auto hide-scrollbar" style={{ maxHeight: '70vh' }}>
              <div className="grid md:grid-cols-2 gap-5">
                {/* Left column */}
                <div className="space-y-4">
                  {/* Asset Selection - Searchable Dropdown (or read-only when editing) */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Asset <span className="text-red-400">*</span>
                    </label>
                    {editingRepairId ? (
                      <div className="w-full px-3 py-2 bg-slate-900/60 border border-slate-700 rounded-lg text-slate-400 text-sm">
                        {selectedAssetDetails ? 
                          `${selectedAssetDetails.id} - ${selectedAssetDetails.name || selectedAssetDetails.assetType || selectedAssetDetails.type || 'Unknown'}` 
                          : newTicketForm.assetId}
                      </div>
                    ) : (
                      <>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <SearchableSelect
                              options={allAssets.map(asset => ({
                                value: asset.id,
                                label: `${asset.id} - ${asset.name || asset.assetType || asset.type || 'Unknown'}`,
                                asset: asset
                              }))}
                              value={newTicketForm.assetId}
                              onChange={(value) => handleFormChange('assetId', value)}
                              placeholder="Search and select an asset..."
                              searchPlaceholder="Type to search assets..."
                              className="w-full"
                              required
                              isDark={isDark}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowScanner(true)}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap"
                            title="Scan PLX Code"
                          >
                            <Camera size={18} />
                            Scan
                          </button>
                        </div>
                        {allAssets.length === 0 && (
                          <p className="text-amber-400 text-xs mt-2 flex items-center gap-1">
                            <AlertCircle size={12} />
                            No assets available. Please create an asset first.
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Auto-filled asset info */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-3">
                      <div className="text-xs text-slate-500 uppercase mb-1">Owner Name</div>
                      <div className="text-sm text-slate-200 font-medium">{selectedAssetDetails?.customerName || '—'}</div>
                    </div>
                    <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-3">
                      <div className="text-xs text-slate-500 uppercase mb-1">Brand</div>
                      <div className="text-sm text-slate-200 font-medium">{selectedAssetDetails?.brand || '—'}</div>
                    </div>
                    <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-3">
                      <div className="text-xs text-slate-500 uppercase mb-1">Asset Name</div>
                      <div className="text-sm text-slate-200 font-medium">{selectedAssetDetails?.assetName || '—'}</div>
                    </div>
                    <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-3">
                      <div className="text-xs text-slate-500 uppercase mb-1">Asset Number</div>
                      <div className="text-sm text-slate-200 font-medium">{selectedAssetDetails?.assetNumber || '—'}</div>
                    </div>
                  </div>

                  {/* Title & Description */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Title <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={newTicketForm.title}
                      onChange={(e) => handleFormChange('title', e.target.value)}
                      placeholder="Brief description of the issue"
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2.5 text-slate-200 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={newTicketForm.description}
                      onChange={(e) => handleFormChange('description', e.target.value)}
                      placeholder="Detailed description of the repair needed..."
                      rows={3}
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2.5 text-slate-200 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
                    />
                  </div>

                  {/* Attachments Section */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      <span className="flex items-center gap-1">
                        <Image size={14} />
                        Attachments / Pictures
                      </span>
                    </label>
                    <input
                      ref={attachmentInputRef}
                      type="file"
                      accept="image/*,.pdf,.doc,.docx"
                      multiple
                      onChange={handleAttachmentSelect}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => attachmentInputRef.current?.click()}
                      className={`w-full px-4 py-3 border border-dashed rounded-lg transition-colors flex items-center justify-center gap-2 ${
                        isDark
                          ? 'bg-slate-950 border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-600'
                          : 'bg-gray-50 border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      <Upload size={18} />
                      Click to upload attachments
                    </button>
                    
                    {/* Attachment Previews */}
                    {attachments.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {attachments.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 p-2 bg-slate-900/40 border border-slate-800 rounded-lg"
                          >
                            {file.type.startsWith('image/') ? (
                              <Image size={16} className="text-blue-400" />
                            ) : (
                              <FileText size={16} className="text-slate-400" />
                            )}
                            <span className="flex-1 text-sm text-slate-300 truncate">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveAttachment(index)}
                              className="p-1 hover:bg-red-900/30 rounded text-red-400"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Damage Details Section */}
                  <div className="border border-slate-700 rounded-lg p-4 space-y-3">
                    <h3 className="text-sm font-medium text-slate-300">Damage Details</h3>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Damage Type</label>
                        <select
                          value={newTicketForm.damageDetails.type}
                          onChange={(e) => handleDamageDetailsChange('type', e.target.value)}
                          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500 cursor-pointer"
                        >
                          <option value="">Select type</option>
                          {DAMAGE_TYPES.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Damage Location</label>
                        <input
                          type="text"
                          value={newTicketForm.damageDetails.location}
                          onChange={(e) => handleDamageDetailsChange('location', e.target.value)}
                          placeholder="e.g., Left side, Top panel"
                          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Damage Description</label>
                      <textarea
                        value={newTicketForm.damageDetails.description}
                        onChange={(e) => handleDamageDetailsChange('description', e.target.value)}
                        placeholder="Describe the damage in detail..."
                        rows={2}
                        className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500 resize-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Right column */}
                <div className="space-y-4">
                  {/* Priority + Notes */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Priority
                        </label>
                        <select
                          value={newTicketForm.priority}
                          onChange={(e) => handleFormChange('priority', e.target.value)}
                          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2.5 text-slate-200 focus:outline-none focus:border-blue-500 cursor-pointer"
                        >
                          {PRIORITY_OPTIONS.map(priority => (
                            <option key={priority} value={priority}>{priority}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Initial Status
                        </label>
                        <select
                          value={newTicketForm.status}
                          onChange={(e) => handleFormChange('status', e.target.value)}
                          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2.5 text-slate-200 focus:outline-none focus:border-blue-500 cursor-pointer"
                        >
                          {STATUS_OPTIONS.map(status => (
                            <option key={status} value={status}>{status}</option>
                          ))}
                        </select>
                        <p className="text-xs text-slate-500 mt-1">Defaults to "Needs Approval" so the customer can approve and return.</p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        <span className="flex items-center gap-1">
                          <FileText size={14} />
                          Notes
                        </span>
                      </label>
                      <textarea
                        value={newTicketForm.notes}
                        onChange={(e) => handleFormChange('notes', e.target.value)}
                        placeholder="Include any customer-facing notes..."
                        rows={5}
                        className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2.5 text-slate-200 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
                      />
                    </div>
                  </div>

                  {/* Template Toggle */}
                  {availableTemplates.length > 0 && (
                    <div className="flex items-center justify-between p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <div>
                        <label className="text-sm font-medium text-slate-300">Use Template</label>
                        <p className="text-xs text-slate-500 mt-0.5">Load custom repair fields from a template</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newValue = !useTemplate;
                          setUseTemplate(newValue);
                          if (!newValue) {
                            setSelectedNewTicketTemplate(null);
                            // Reset to default REPAIR_ITEMS
                            setRepairChecklist(() => {
                              const reset = {};
                              REPAIR_ITEMS.forEach(item => { reset[item.id] = 'Good'; });
                              return reset;
                            });
                          }
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          useTemplate ? 'bg-blue-600' : 'bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            useTemplate ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  )}

                  {/* Template Selector (only show if toggle is on) */}
                  {useTemplate && availableTemplates.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Select Template
                      </label>
                      <select
                        value={selectedNewTicketTemplate?.id || ''}
                        onChange={(e) => {
                          const template = availableTemplates.find(t => t.id === e.target.value);
                          handleNewTicketTemplateSelect(template);
                        }}
                        className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500 cursor-pointer"
                      >
                        <option value="">Select a template...</option>
                        {availableTemplates.map(template => (
                          <option key={template.id} value={template.id}>
                            {template.template_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Repair Checklist Fields (from template or default) */}
                  <div className="border border-slate-800 rounded-xl p-4 space-y-4 bg-slate-900/40">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-slate-200">
                        Basic Repair Fields{useTemplate && selectedNewTicketTemplate ? ` - ${selectedNewTicketTemplate.template_name}` : ''}
                      </h3>
                      <div className="text-xs text-slate-500">
                        Toggle items to "Damaged"
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {(useTemplate && selectedNewTicketTemplate ? selectedNewTicketTemplate.checklist_items : REPAIR_ITEMS).map(item => {
                        const currentValue = repairChecklist[item.id];
                        const isDamaged = currentValue === 'Damaged';
                        
                        return (
                          <button
                            type="button"
                            key={item.id}
                            onClick={() => toggleRepairItem(item.id)}
                            className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-colors ${
                              isDamaged
                                ? 'bg-red-900/30 border-red-500/40 text-red-200'
                                : 'bg-slate-800/40 border-slate-700 text-slate-200 hover:border-slate-600'
                            }`}
                          >
                            <div className="text-left">
                              <div className="text-sm font-medium">{item.label}</div>
                              <div className="text-[11px] text-slate-400">
                                Default: {item.input_type === 'select' && item.options ? item.options[0] : 'Good'}
                              </div>
                            </div>
                            <div className="flex flex-col items-end">
                              <span className="text-xs text-slate-400">${item.default_cost || item.cost}</span>
                              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${
                                isDamaged
                                  ? 'border-red-400 text-red-200 bg-red-500/10'
                                  : 'border-emerald-400 text-emerald-200 bg-emerald-500/10'
                              }`}>
                                {isDamaged ? 'Damaged' : (item.options?.[0] || 'Good')}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Other Damages */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Other Damages</label>
                        <input
                          type="text"
                          value={otherDamage.description}
                          onChange={(e) => setOtherDamage(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Describe any other damage"
                          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Other Damage Cost</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={otherDamage.cost}
                          onChange={(e) => setOtherDamage(prev => ({ ...prev, cost: e.target.value }))}
                          placeholder="0.00"
                          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {/* Auto-calculated total */}
                    <div className="flex items-center justify-between border-t border-slate-800 pt-3">
                      <div className="text-sm text-slate-400 flex items-center gap-2">
                        <DollarSign size={14} />
                        Auto-calculated repair total
                      </div>
                      <div className="text-lg font-semibold text-emerald-400">
                        ${computedRepairTotal.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 pt-4 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => {
                    setShowNewTicketModal(false);
                    setEditingRepairId(null);
                    resetRepairForm();
                  }}
                  className="flex-1 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || allAssets.length === 0}
                  className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      {editingRepairId ? 'Sending to Owner...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      {editingRepairId ? <Send size={16} /> : <Plus size={16} />}
                      {editingRepairId ? 'Send to Owner' : 'Create Ticket'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ticket Details Modal with Comments - DISABLED: Now using summary panel instead */}
      {false && selectedTicket && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-800/30 border border-gray-700 rounded-xl w-full max-w-4xl mx-4 shadow-2xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-800 shrink-0">
              <div>
                <h2 className="text-xl font-bold text-white">{selectedTicket.title}</h2>
                <p className="text-sm text-slate-400 mt-1">
                  Ticket #{selectedTicket.id.slice(0, 8)} • {selectedTicket.status}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* Chat Toggle for Owner-Assigned Vendor Communication */}
                <ChatToggle repairId={selectedTicket.id} size="md" />
                <button 
                  onClick={() => { setSelectedTicket(null); setComments([]); setNewComment(''); setLightboxImage(null); }}
                  className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <X size={20} className="text-slate-400" />
                </button>
              </div>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 hide-scrollbar">
              {/* Ticket Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-xs text-slate-500 uppercase">Priority</span>
                  <div className="flex items-center gap-2 mt-1">
                    {(isVendor || isCustomer || isAdmin) && (
                      <select
                        value={selectedTicket.priority}
                        onChange={async (e) => {
                          const newPriority = e.target.value;
                          try {
                            await updateRepair(selectedTicket.id, { priority: newPriority });
                            setSelectedTicket(prev => ({ ...prev, priority: newPriority }));
                            setRepairs(prev => prev.map(r => r.id === selectedTicket.id ? { ...r, priority: newPriority } : r));
                          } catch (error) {
                            console.error('Error updating priority:', error);
                            alert('Failed to update priority');
                          }
                        }}
                        className={`text-sm font-medium rounded px-2 py-1 border ${
                          selectedTicket.priority === 'Critical' || selectedTicket.priority === 'High' ? 'text-red-400 border-red-500/30 bg-red-500/10' :
                          selectedTicket.priority === 'Medium' ? 'text-amber-400 border-amber-500/30 bg-amber-500/10' : 'text-blue-400 border-blue-500/30 bg-blue-500/10'
                        } ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-300'}`}
                      >
                        {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    )}
                    {!isVendor && !isCustomer && !isAdmin && (
                      <p className={`text-sm font-medium ${
                        selectedTicket.priority === 'Critical' || selectedTicket.priority === 'High' ? 'text-red-400' :
                        selectedTicket.priority === 'Medium' ? 'text-amber-400' : 'text-blue-400'
                      }`}>{selectedTicket.priority}</p>
                    )}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-slate-500 uppercase">Asset</span>
                  <p className="text-sm text-slate-200">{selectedTicket.asset_name || selectedTicket.asset_id}</p>
                </div>
                <div>
                  <span className="text-xs text-slate-500 uppercase">Status</span>
                  <div className="flex items-center gap-2 mt-1">
                    {(isVendor || isCustomer || isAdmin) && (
                      <select
                        value={selectedTicket.status}
                        onChange={async (e) => {
                          const newStatus = e.target.value;
                          try {
                            if (newStatus === 'Finalized' && isCustomer) {
                              // Owner can finalize (scrap) - show confirmation
                              if (window.confirm('Are you sure you want to scrap this repair? This action cannot be undone.')) {
                                await moveRepair(selectedTicket.id, 'Finalized');
                                setSelectedTicket(prev => ({ ...prev, status: 'Finalized' }));
                              }
                            } else {
                              await moveRepair(selectedTicket.id, newStatus);
                              setSelectedTicket(prev => ({ ...prev, status: newStatus }));
                            }
                          } catch (error) {
                            console.error('Error updating status:', error);
                            alert('Failed to update status');
                          }
                        }}
                        className={`text-sm font-medium rounded px-2 py-1 border ${
                          isDark ? 'bg-slate-900 border-slate-700 text-slate-200' : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                        {effectiveIsCustomer && (
                          <>
                            <option value="In Repair">Approve</option>
                            <option value="Finalized">Scrap</option>
                            <option value="Sent to Vendor">Decline</option>
                          </>
                        )}
                      </select>
                    )}
                    {!isVendor && !isCustomer && !isAdmin && (
                      <p className="text-sm text-slate-200">{selectedTicket.status}</p>
                    )}
                  </div>
                </div>
                {selectedTicket.total_repair_cost !== null && selectedTicket.total_repair_cost !== undefined && (
                  <div>
                    <span className="text-xs text-slate-500 uppercase">Repair Cost</span>
                    <div className="flex items-center gap-2 mt-1">
                      {isVendor && (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={selectedTicket.total_repair_cost || ''}
                          onChange={async (e) => {
                            const newCost = parseFloat(e.target.value) || 0;
                            try {
                              await updateRepair(selectedTicket.id, { totalRepairCost: newCost });
                              setSelectedTicket(prev => ({ ...prev, total_repair_cost: newCost }));
                              setRepairs(prev => prev.map(r => r.id === selectedTicket.id ? { ...r, total_repair_cost: newCost } : r));
                            } catch (error) {
                              console.error('Error updating cost:', error);
                              alert('Failed to update cost');
                            }
                          }}
                          className="text-sm text-emerald-400 font-medium bg-slate-950 border border-slate-700 rounded px-2 py-1 w-32"
                        />
                      )}
                      {!isVendor && (
                        <p className="text-sm text-emerald-400 font-medium">${parseFloat(selectedTicket.total_repair_cost || 0).toFixed(2)}</p>
                      )}
                    </div>
                  </div>
                )}
                {selectedTicket.vendor_name && (
                  <div>
                    <span className="text-xs text-slate-500 uppercase">Vendor</span>
                    <p className="text-sm text-slate-200">{selectedTicket.vendor_name}</p>
                  </div>
                )}
              </div>

              {/* Description */}
              {selectedTicket.description && (
                <div>
                  <span className="text-xs text-slate-500 uppercase">Description</span>
                  <p className="text-sm text-slate-300 mt-1">{selectedTicket.description}</p>
                </div>
              )}

              {/* Notes - Editable for Assigned Vendor and Owner */}
              <div>
                <span className="text-xs text-slate-500 uppercase">Notes</span>
                {(isVendor || isCustomer || isAdmin) ? (
                  <textarea
                    value={selectedTicket.notes || ''}
                    onChange={async (e) => {
                      const newNotes = e.target.value;
                      try {
                        await updateRepair(selectedTicket.id, { notes: newNotes });
                        setSelectedTicket(prev => ({ ...prev, notes: newNotes }));
                        setRepairs(prev => prev.map(r => r.id === selectedTicket.id ? { ...r, notes: newNotes } : r));
                      } catch (error) {
                        console.error('Error updating notes:', error);
                        alert('Failed to update notes');
                      }
                    }}
                    className="w-full mt-1 bg-slate-950 border border-slate-700 rounded-lg px-4 py-2.5 text-sm text-slate-200 focus:outline-none focus:border-blue-500 resize-none"
                    rows={3}
                    placeholder="Add notes..."
                  />
                ) : (
                  <p className="text-sm text-slate-300 mt-1">{selectedTicket.notes || 'No notes'}</p>
                )}
              </div>

              {/* Attachments */}
              <div>
                <span className="text-xs text-slate-500 uppercase flex items-center gap-1 mb-2">
                  <Image size={12} />
                  Attachments {repairFiles[selectedTicket.id] ? `(${repairFiles[selectedTicket.id].length})` : '(0)'}
                </span>
                {repairFiles[selectedTicket.id] && repairFiles[selectedTicket.id].length > 0 ? (
                  <div className="grid grid-cols-3 gap-3">
                    {repairFiles[selectedTicket.id].map((file, index) => {
                      // Handle both file objects (from media_files) and attachment objects (from messages/comments)
                      // Skip gs:// URLs - we need HTTP signed URLs
                      let imageUrl = file.viewUrl || file.view_url || file.url;
                      // Filter out gs:// URLs
                      if (imageUrl && imageUrl.startsWith('gs://')) {
                        imageUrl = null;
                      }
                      const filename = file.original_filename || file.filename || 'Image';
                      const isImage = file.mime_type?.startsWith('image/') || file.type?.startsWith('image/') || (imageUrl && !imageUrl.startsWith('gs://'));
                      
                      // Skip if no valid image URL
                      if (!isImage || !imageUrl) return null;
                      
                      return (
                        <div
                          key={file.id || `attachment-${index}`}
                          className="bg-slate-900/40 border border-slate-800 rounded-lg p-3 hover:border-slate-600 transition-colors cursor-pointer"
                          onClick={() => {
                            setLightboxImage({ url: imageUrl, filename });
                          }}
                        >
                          <img
                            src={imageUrl}
                            alt={filename}
                            className="w-full h-32 object-cover rounded mb-2"
                            onError={(e) => { 
                              e.target.style.display = 'none';
                              e.target.parentElement.querySelector('.error-placeholder')?.classList.remove('hidden');
                            }}
                          />
                          <div className="hidden error-placeholder w-full h-32 bg-slate-800 rounded mb-2">
                            <div className="flex items-center justify-center h-full">
                              <FileText size={32} className="text-slate-600" />
                            </div>
                          </div>
                          <p className="text-xs text-slate-300 truncate" title={filename}>
                            {filename}
                          </p>
                        </div>
                      );
                    }).filter(Boolean)}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 italic">No attachments</p>
                )}
              </div>

              {/* Lightbox for images */}
              {lightboxImage && (
                <div
                  className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
                  onClick={() => setLightboxImage(null)}
                >
                  <button
                    onClick={() => setLightboxImage(null)}
                    className="absolute top-4 right-4 p-2 bg-slate-800 hover:bg-slate-700 rounded-full transition-colors z-10"
                  >
                    <X size={24} className="text-white" />
                  </button>
                  <img
                    src={lightboxImage.url}
                    alt={lightboxImage.filename || 'Image'}
                    className="max-w-full max-h-full object-contain"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="absolute bottom-4 left-4 right-4 text-center">
                    <p className="text-white text-sm bg-black/60 rounded px-4 py-2 inline-block">
                      {lightboxImage.filename || 'Image'}
                    </p>
                  </div>
                </div>
              )}

              {/* Damage Details */}
              {selectedTicket.damage_details && Object.keys(selectedTicket.damage_details).length > 0 && (
                <div className="bg-gray-800/30 rounded-lg p-4 border border-gray-700">
                  <span className="text-xs text-slate-500 uppercase">Damage Details</span>
                  <div className="mt-2 space-y-2">
                    {selectedTicket.damage_details.type && (
                      <p className="text-sm text-slate-300"><span className="text-slate-500">Type:</span> {selectedTicket.damage_details.type}</p>
                    )}
                    {selectedTicket.damage_details.location && (
                      <p className="text-sm text-slate-300"><span className="text-slate-500">Location:</span> {selectedTicket.damage_details.location}</p>
                    )}
                    {selectedTicket.damage_details.description && (
                      <p className="text-sm text-slate-300"><span className="text-slate-500">Description:</span> {selectedTicket.damage_details.description}</p>
                    )}
                    {selectedTicket.damage_details.checklist && Array.isArray(selectedTicket.damage_details.checklist) && (
                      <div className="mt-3 pt-3 border-t border-slate-700">
                        <p className="text-xs text-slate-500 uppercase mb-2">Repair Checklist</p>
                        <div className="grid grid-cols-2 gap-2">
                          {selectedTicket.damage_details.checklist.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-xs">
                              <span className="text-slate-400">{item.label}:</span>
                              <span className={item.status === 'Damaged' ? 'text-red-400' : 'text-emerald-400'}>
                                {item.status} {item.status === 'Damaged' && `($${item.cost})`}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Comments Section */}
              <div className="border-t border-slate-800 pt-4">
                <div className="flex items-center gap-2 mb-4">
                  <MessageSquare size={16} className="text-slate-400" />
                  <span className="text-sm font-medium text-slate-300">Comments ({comments.length})</span>
                </div>

                {loadingComments ? (
                  <div className="text-center py-4 text-slate-500 text-sm">Loading comments...</div>
                ) : (
                  <div className="space-y-3 max-h-48 overflow-y-auto mb-4 hide-scrollbar">
                    {comments.length === 0 ? (
                      <p className="text-slate-500 text-sm text-center py-4">No comments yet</p>
                    ) : (
                      comments.map(comment => {
                        // Format author name nicely
                        const formatAuthorName = () => {
                          // Try author_name first (from backend)
                          if (comment.author_name && comment.author_name !== 'Unknown') {
                            if (comment.author_name.startsWith('User ')) {
                              return comment.author_name;
                            }
                            return comment.author_name
                              .split(/[._-]/)
                              .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
                              .join(' ');
                          }
                          
                          // Fallback to email prefix
                          if (comment.author_email && !comment.author_email.startsWith('deleted_user_')) {
                            const emailPrefix = comment.author_email.split('@')[0];
                            if (emailPrefix) {
                              return emailPrefix
                                .split(/[._-]/)
                                .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
                                .join(' ');
                            }
                          }
                          
                          return 'User ' + (comment.author_id?.substring(0, 8) || 'Unknown');
                        };
                        
                        return (
                          <div key={comment.id} className="bg-gray-800/30 rounded-lg p-3 border border-gray-700 group">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-medium text-blue-400">
                                    {formatAuthorName()}
                                  </span>
                                  <span className="text-xs text-slate-600">
                                    {new Date(comment.created_at).toLocaleDateString()} {new Date(comment.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                                <p className="text-sm text-slate-300">{comment.comment_text}</p>
                                {comment.attachments && comment.attachments.length > 0 && (
                                  <AttachmentDisplay 
                                    attachments={typeof comment.attachments === 'string' ? JSON.parse(comment.attachments) : comment.attachments}
                                    className="mt-2"
                                  />
                                )}
                              </div>
                              <button 
                                onClick={() => handleDeleteComment(comment.id)}
                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-900/30 rounded transition-all"
                              >
                                <Trash2 size={14} className="text-red-400" />
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}

                {/* Add Comment Input */}
                <div className="space-y-3">
                  <ImageAttachment
                    onAttachmentsChange={setCommentAttachments}
                    attachments={commentAttachments}
                    maxFiles={5}
                    uploadEndpoint="/api/repairs/comments/upload-attachment"
                    metadata={{ repairId: selectedTicket?.id }}
                  />
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                      placeholder="Add a comment..."
                      className="flex-1 bg-slate-950 border border-slate-700 rounded-lg px-4 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
                    />
                    <button
                      onClick={handleAddComment}
                      disabled={!newComment.trim() && commentAttachments.length === 0}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Repair Summary Report Modal */}
      {showSummaryReport && (
        <RepairSummaryReport
          ticket={showSummaryReport}
          onClose={() => setShowSummaryReport(null)}
          isDark={isDark}
        />
      )}

      {/* Vendor Inspection Form Modal */}
      {showVendorInspectionForm && vendorInspectionTicket && (
        <VendorInspectionForm 
          ticket={vendorInspectionTicket}
          onCancel={() => {
            setShowVendorInspectionForm(false);
            setVendorInspectionTicket(null);
          }}
          onComplete={() => {
            setShowVendorInspectionForm(false);
            setVendorInspectionTicket(null);
          }}
          isNewInspection={vendorInspectionTicket.id === 'new'}
        />
      )}

      {/* PLX Scanner Modal */}
      {showScanner && (
        <PLXScanner
          onScan={handlePLXScan}
          onClose={() => setShowScanner(false)}
          isDark={isDark}
        />
      )}
      </div>
    </FeatureGate>
  );
}
